import { formatCurrency, formatNumber } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { SmsPackageOption } from '@common/components';
import { AutoPayCardPaidEvent } from '@common/components/auto-pay-card/auto-pay-card.component';
import {
    AutoPayCardMixChargeIssue,
    CardChargeUnresolvedOutcome,
    COUPON_HOLD_EXPIRED_FALLBACK_MESSAGE,
    COUPON_HOLD_EXPIRED_TITLE,
    CouponDesglose,
    CouponPreviewResponse,
    FiscalOffer,
    FiscalQuota,
    PaymentVerificationOutcome,
    Plan,
    PlanCouponSelection,
    PlanCouponSummary,
    PlanMigrationNotice,
    PlansBundle,
    PlansPaymentMethod,
    VerifyPaymentRequest,
    VerifyPaymentResponseData,
} from '@common/models';
import { AnalyticsService, CommerceService, ControlNumbersStatusService, FiscalAccessService, PaymentPlanModalRequestService, PlansService, ToastService } from '@common/services';
import { BuySmsRequest, SmsService } from '@common/services/sms.service';
import { CONTROL_NUMBERS_FREE_MODE_MESSAGE, CONTROL_NUMBERS_FREE_MODE_TITLE, isControlNumbersFreeModeError } from '@common/utils/control-numbers.util';
import {
    canManageExtraUserSeats,
    EXTRA_USERS_MIX_RENEWAL_DUE,
    EXTRA_USERS_MIX_RENEWAL_DUE_MESSAGE,
    EXTRA_USERS_SALES_DISABLED_MESSAGE,
    EXTRA_USERS_SALES_DISABLED_TITLE,
    EXTRA_USERS_SALES_ENABLED,
    extraUsersBaseUSD,
    extraUsersDeltaAmountUSD,
    extraUsersDeltaBaseUSD,
    getExtraUserPriceUsd,
    isExtraUsersSalesDisabledError,
    minPurchasedUsersForOccupancy,
    mixExtraUsersMonths,
    normalizePurchasedUsers,
    planIncludedUsers,
} from '@common/utils/extra-users.util';
import { buildFiscalOfferCheckoutProps, FISCAL_OFFER_CHECKOUT_EVENTS, FiscalOfferCheckoutChannel } from '@common/utils/fiscal-offer-checkout-analytics.util';
import {
    buildFiscalQuotaOptions,
    FIRST_PAYMENT_FISCAL_QUOTA_CHANGE_UNSUPPORTED,
    FISCAL_QUOTA_REJECTED,
    FISCAL_SALES_DISABLED,
    fiscalQuotaAmountDelta,
    fiscalQuotaIssue,
    FiscalQuotaIssueCode,
    FiscalQuotaOption,
    fiscalQuotaPeriodBaseUsd,
    isFiscalOfferPlan,
    normalizeFiscalOffer,
    normalizeFiscalQuota,
    readBilledFiscalQuota,
    resolveRequestedFiscalQuota,
} from '@common/utils/fiscal-quota.util';
import { buildMixCartProps, MIX_PURCHASE_EVENTS, MixCartAnalyticsProps, mixCartItems, MixPurchaseBlockedStage } from '@common/utils/mix-purchase-analytics.util';
import { MIX_LINE_LABELS, mixBankQuoteInstant, mixLineUnit, MixQuote, MixQuoteLine, quoteMixPurchase, resolveMixBankQuoteDay } from '@common/utils/quote-mix-purchase.util';
import { isValidTaxId } from '@common/utils/rif-validator.util';
import { UserService } from '@modules/auth/services';
import { ModalDismissReasons, NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDateStruct, NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { environment } from 'environments/environment';
import lottie from 'lottie-web';
import moment from 'moment';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, shareReplay, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

const DEFAULT_MANUAL_REVIEW_ATTEMPTS = 3;

const AMOUNT_MISMATCH_OUTCOMES: PaymentVerificationOutcome[] = ['amount_below_tolerance', 'amount_mismatch', 'overpayment_within_tolerance', 'overpayment_above_tolerance'];

const initialPayData: any = {
    billPlanId: '',
    paymentDate: '',
    paidDate: '',
    paymentMethod: '',
    ref: '',
    amount: 0,
    amountPlan: 0,
    amountVES: 0,
    exchangeRateBCV: 0,
    issuing: '',
    comments: '',
    paymentFrequency: 'mensual',
    /** Monto IVA en USD (mismo sentido que `Billplan.plus` en historial de facturas). */
    plus: 0,
};

/**
 * Copy única para el estado "tenant en pago con factura pendiente": lo usan el aviso
 * del paso 1, el toast del CTA bloqueado y el 409 "Cambio no permitido" del backend
 * (cuyo mensaje habla de "mid-cycle", jerga interna que no mostramos al cliente).
 */
const PENDING_BILL_BLOCKS_PLAN_CHANGE_MESSAGE = 'Tienes una factura pendiente por pagar. Págala con tu plan actual y luego podrás cambiar de plan o frecuencia.';

/** Espera antes de validar el código en vivo, para no pegarle al backend por tecla. */
const COUPON_PREVIEW_DEBOUNCE_MS = 500;
/** Fallback cuando el rechazo llega sin `message` (error de red, 500 sin cuerpo). */
const COUPON_PREVIEW_FALLBACK_MESSAGE = 'No pudimos validar el código. Inténtalo de nuevo.';
const COUPON_APPLY_FALLBACK_MESSAGE = 'No pudimos aplicar el cupón a tu factura. Inténtalo de nuevo.';
/** El canje ya está sellado en la factura y retirarlo no depende del frontEnd. */
const COUPON_SEALED_REMOVAL_MESSAGE = 'El descuento ya está aplicado a tu factura y no se puede quitar desde aquí.';

/** Título del 409 de api-admin cuando la factura origen del cambio no está en `pago`. */
const MID_CYCLE_NOT_ALLOWED_TITLE = 'Cambio no permitido';

/** `message` literal del 409 «Cupón y cupos no combinables» de api-admin (ADM-743). */
const COUPON_SEATS_CARD_BLOCKED_MESSAGE =
    'No puedes cambiar los cupos de usuarios adicionales y el plan o la frecuencia con un cupón aplicado en un pago con tarjeta. Mantén los cupos actuales o paga por pago móvil o Débito Inmediato.';
/** Mismo aviso cuando Débito Inmediato no se ofrece a este comercio (flag apagado). */
const COUPON_SEATS_CARD_BLOCKED_MESSAGE_WITHOUT_DEBIT =
    'No puedes cambiar los cupos de usuarios adicionales y el plan o la frecuencia con un cupón aplicado en un pago con tarjeta. Mantén los cupos actuales o paga por pago móvil.';

/** `message` literal del 409 `COUPON_FISCAL_QUOTA_CHANGE_UNSUPPORTED` de api-admin (ADM-740). */
const COUPON_FISCAL_QUOTA_CARD_BLOCKED_MESSAGE =
    'No puedes cambiar la cantidad de facturas y el plan o la frecuencia con un cupón aplicado en un pago con tarjeta. Mantén la cantidad actual o paga por pago móvil o Débito Inmediato.';
/** Mismo aviso cuando Débito Inmediato no se ofrece a este comercio (flag apagado). */
const COUPON_FISCAL_QUOTA_CARD_BLOCKED_MESSAGE_WITHOUT_DEBIT =
    'No puedes cambiar la cantidad de facturas y el plan o la frecuencia con un cupón aplicado en un pago con tarjeta. Mantén la cantidad actual o paga por pago móvil.';

/** Opciones de frecuencia (UI; extraer a componente de paso 1). */
export type PayPlanFrequencyId = 'monthly' | 'quarterly' | 'yearly' | 'semiannual';

/** Modo de apertura del modal de plan / SMS. */
export type PayPlanModalMode = 'pay' | 'change-plan' | 'buy-sms' | 'mix';

export interface MixPayCart {
    smsQuantity: number;
    smsPriceUsd?: number;
    /** `quantity` del paquete de números de control (ADM-732); 0/ausente = sin paquete. */
    controlNumbersQuantity?: number;
    /** Precio USD neto del paquete de números de control. */
    controlNumbersPriceUsd?: number;
    extraUsersToAdd: number;
    extraUserPriceUsd?: number;
    includeSetup: boolean;
    amountUsd: number;
    subtotalUsd: number;
    ivaUsd: number;
    retentionUsd?: number;
}

@Component({
    selector: 'fina-form-pay-plan',
    templateUrl: './form-pay-plan.component.html',
    styleUrls: ['form-pay-plan.component.scss'],
})
export class FormPayPlanComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly destroy$: Subject<void> = new Subject<void>();
    /** Dispara la consulta del estado de la tarjeta (ver pipeline en `ngOnInit`). */
    private readonly loadCard$: Subject<void> = new Subject<void>();
    /** Día de pago del mix ('YYYY-MM-DD') cuya tasa BCV hay que cargar (ver pipeline en `ngOnInit`). */
    private readonly mixRateDay$: Subject<string | undefined> = new Subject<string | undefined>();
    // IDs de métodos de pago a filtrar condicionalmente
    private readonly BNC_EFECTIVO_STAGING_ID = '68e6adcaf7466343a2170294';
    private readonly BNC_EFECTIVO_PROD_ID = '68e87cf3a219dbc1869a1bd3';
    /** IVA general (16%) sobre base neta del plan, cuando el tenant tiene `commerce.plus`. */
    private readonly planPaymentIvaRate = 0.16;
    /** Fee unitario mid-cycle por mes restante del ciclo ($5 × meses). */
    private readonly midCyclePlanChangeFeePerMonthUsd = 5;
    /** Aviso del paso 1 cuando una factura pendiente bloquea el cambio de plan. */
    readonly pendingBillBlocksPlanChangeMessage = PENDING_BILL_BLOCKS_PLAN_CHANGE_MESSAGE;
    @Input() isPlan: boolean = false;
    @Input() title: string = 'Registrar pago';
    /**
     * Apariencia del botón lanzador cuando `isPlan`. 'secondary' lo muestra como
     * botón outline (menos prominente) — útil cuando el cobro automático está
     * activo y "Pagar ahora" deja de ser la acción principal.
     */
    @Input() triggerVariant: 'primary' | 'secondary' = 'primary';
    /**
     * Muestra el botón lanzador propio del componente. Ponlo en `false` cuando la
     * modal se abra programáticamente desde afuera (p. ej. `openPayFlow()`),
     * reutilizando una sola instancia para varios botones.
     */
    @Input() showTrigger = true;
    /**
     * Una compra mix quedó registrada (reportada o cobrada con tarjeta). Quien abrió
     * el carrito («Mejora tu Fina») lo vacía: volver a pagarlo daría 409 «Compra en proceso».
     */
    @Output() mixPurchaseRegistered = new EventEmitter<void>();
    @ViewChild('modalForm') payData: TemplateRef<any>;

    payFormData: any = { ...initialPayData };
    loading: boolean = false;
    loadingPlans: boolean = true;
    loadingPaymentMethods: boolean = true;
    readonly skeletonSlots = [1, 2, 3];
    paymentMethods: PlansPaymentMethod[] = [];
    paymentMethodSelected: PlansPaymentMethod = null;
    plans: Plan[] = [];

    // ── Migración a facturación formal ──
    // El backend decide a quién avisarle (`GET /plans/payment-methods`): el
    // front solo pinta. `bankAccountChanged` se apaga solo cuando el comercio
    // paga por primera vez a la cuenta nueva.
    migrationNotice: PlanMigrationNotice = { bankAccountChanged: false, requiresFiscalInfo: false };
    readonly taxIdTypes = ['J', 'V', 'E', 'G', 'P'];
    fiscalForm: { taxIdType: string; taxIdNumber: string; address: string } = {
        taxIdType: 'J',
        taxIdNumber: '',
        address: '',
    };
    savingFiscalInfo = false;
    fiscalInfoSaved = false;

    // Validation state
    isValidated: boolean = false;
    isValidating: boolean = false;
    validationAttempts: number = 0;
    manualReviewAttempts: number = DEFAULT_MANUAL_REVIEW_ATTEMPTS;
    validationTimer: number = 0;
    validationTimerInterval: any = null;
    validationError: string = '';
    canSkipValidation: boolean = false;
    /** True when skip was unlocked by an amount mismatch (below or over expected). */
    skipDueToAmountMismatch: boolean = false;
    /** True when skip was unlocked by staging skip (shows staging-specific message). */
    skipDueToStaging: boolean = false;
    /** True cuando la aplicación local obliga revisión aunque el banco valide. */
    skipDueToApplicationReview: boolean = false;
    lastPaymentDate: string = '';
    private acceptedValidationFingerprint: string | null = null;

    /** Tras pago exitoso: misma modal muestra encuesta (clientes que vuelven / over_60). */
    postPaymentSurveyStep: boolean = false;
    planPaymentSuccessMessage: string = '';
    private activePayModalRef: NgbModalRef | null = null;
    private payPlanConfettiAnimation: any = null;

    // fechas
    hoveredDate: NgbDate | null = null;
    fromDate: NgbDate;
    maxDate: NgbDate;
    paymentDateInput: string;
    toDate: NgbDate | null = null;
    model: NgbDateStruct;
    indexSelected: number = 0;
    private validationRequestVersion = 0;

    /** True cuando el método de pago elegido es la tarjeta en dólares (Stripe). */
    cardMethodSelected: boolean = false;
    /** True cuando el método elegido es débito inmediato (BNC). */
    debitoInmediatoSelected: boolean = false;
    /**
     * Un débito quedó sin resolver: pudo haber salido dinero de la cuenta del
     * cliente. Mientras esté así, el paso 2 se queda anclado al débito (ver
     * `blockedByUnresolvedDebit`).
     */
    debitoInmediatoUnresolved: boolean = false;
    /**
     * Hay un débito viajando hacia el banco o esperando su liquidación (hasta 2
     * minutos). El dinero puede estar moviéndose: mientras dure, no se puede
     * cambiar de método ni volver al paso 1 (ver `blockedByUnresolvedDebit`).
     */
    debitoInmediatoChargeInFlight: boolean = false;
    /** True cuando el método elegido es pago móvil. Sin preselección: por defecto
     *  ningún método está seleccionado y no se muestra el formulario. */
    pagoMovilSelected: boolean = false;

    /**
     * True cuando el tenant ya tiene una tarjeta activa con cobro automático.
     * En ese caso preseleccionamos la tarjeta en el paso 2 y ocultamos el resto
     * de métodos detrás de un enlace "Cambiar método de pago" (soft-force).
     */
    hasSavedCard: boolean = false;
    /** Revela los demás métodos (pago móvil) cuando el tenant pulsa "Cambiar método de pago". */
    showAllMethods: boolean = false;

    /** Flujo modal en 3 pasos (1: plan, 2: pago/encuesta post-pago, 3: éxito). */
    payModalStep: 1 | 2 | 3 = 1;
    /** _id del plan seleccionado. */
    selectedPlanId: string = '';
    selectedPaymentFrequency: PayPlanFrequencyId = 'monthly';

    /** Código escrito en el campo. Se normaliza a minúsculas (paridad con api-admin). */
    couponCodeInput: string = '';
    /** Cupón del último preview válido; `null` = resumen sin cupón. */
    appliedCoupon: PlanCouponSummary | null = null;
    /**
     * Desglose que devolvió el servidor para ese cupón. Es la **única** fuente del
     * total con cupón: aquí no se recalcula nada (la matemática vive en api-admin).
     */
    couponDesglose: CouponDesglose | null = null;
    /** Rechazo del backend, para mostrar tal cual. No consume el uso del cupón. */
    couponError: string = '';
    isValidatingCoupon: boolean = false;
    isSealingCoupon: boolean = false;
    /** El cupón ya quedó sellado en la factura (`/plans/coupon/apply` con 200). */
    private couponSealed: boolean = false;
    /**
     * Último sello exitoso de ESTA sesión. Sirve para no pintar precio lleno ni
     * cobrar a ciegas si un apply posterior (re-sello o doble click) vuelve 409.
     */
    private sealedCouponSnapshot: {
        code: string;
        coupon: PlanCouponSummary;
        desglose: CouponDesglose;
        selectionKey: string;
        reservedUntil?: string | Date | null;
    } | null = null;
    /** `reservedUntil` del hold vigente (apply 200 o sello de GET /plans). */
    private couponReservedUntil: string | Date | null = null;
    private couponHoldTimer: ReturnType<typeof setTimeout> | null = null;
    /** True mientras el cobro (pago móvil o tarjeta) está en vuelo: no unsealar. */
    private couponHoldPaymentInFlight: boolean = false;
    /** El timer venció durante un cobro; se suelta el hold cuando el cobro falle. */
    private couponHoldExpiredWhilePaying: boolean = false;
    private couponHoldUnsealing: boolean = false;
    /**
     * True tras un apply fallido: no se avanza ni se cobra. No se limpia al
     * quitar el preview; solo un preview/apply nuevos o resetear el modal.
     */
    private couponPaymentBlocked: boolean = false;
    /** Un solo apply en vuelo: el doble click reusa este observable. */
    private sealingInFlight$: Observable<boolean> | null = null;
    /** El panel del cupón nace colapsado tras un enlace (diseño 1A). */
    private couponPanelOpen: boolean = false;
    /** Cada tecla entra aquí; el pipeline de `ngOnInit` hace el debounce y el preview. */
    private readonly couponCodeTyped$: Subject<string> = new Subject<string>();
    /** Sella (o confirma sello) justo antes de que `fina-auto-pay-card` cobre. */
    readonly sealCouponBeforeCardPay = (): Observable<boolean> => {
        if (this.isSmsPurchaseFlow) {
            return of(true);
        }
        if (this.isSealedHoldExpired()) {
            this.expireCouponHold({ unsealOnServer: true });
            return of(false);
        }
        this.couponHoldPaymentInFlight = true;
        return this.ensureCouponSealed$().pipe(
            tap(allowed => {
                if (!allowed) {
                    this.couponHoldPaymentInFlight = false;
                }
            }),
        );
    };

    /** Modo con el que se abrió el modal (controla tabs / SMS). */
    modalMode: PayPlanModalMode = 'pay';
    mixCart: MixPayCart | null = null;
    /** Última cotización del mix: sus `lines` pintan el desglose del paso 2 (paridad con el historial). */
    mixQuote: MixQuote | null = null;
    /**
     * Recarga de la tasa BCV en vuelo al abrir el mix: el resumen de montos y la píldora de tasa
     * se ocultan y «Reportar pago» queda deshabilitado hasta que termine (o venza el timeout de
     * `refreshExchangeRateBCV$`, que emite `null` y sigue con la tasa en memoria).
     */
    refreshingMixRate = false;
    /**
     * Tasa del mix por banco para un día anterior a hoy (el del pago reportado, acotado a ayer).
     * `null` = se cotiza con la tasa de hoy; `rate: null` = la tasa de ese día no cargó y se
     * muestra la de hoy con un aviso. Única fuente de la tasa que usa `syncMixAmountsFromCart`.
     */
    mixDayRate: { day: string; rate: number | null } | null = null;
    readonly mixLineUnit = mixLineUnit;
    /**
     * El checkout mix ya tuvo un resultado terminal (reportado, pagado con tarjeta, cobro
     * pendiente o bloqueado por la renovación): al cerrar no cuenta como abandono.
     */
    private mixCheckoutSettled = false;
    /** Tab activa cuando `showSmsSection` es true. */
    activePlanSmsTab: 'plan' | 'sms' = 'plan';
    /** Paso 2/3 corresponden a compra de paquete SMS (no pago de plan). */
    isSmsPurchaseFlow = false;
    /** Paquete SMS elegido en el paso 1. */
    selectedSmsPackage: SmsPackageOption | null = null;
    /** Mensaje público devuelto por el proxy después de registrar la compra. */
    smsPurchaseSuccessMessage: string = '';
    /** IVA de paquetes SMS (alineado con `fina-sms-messages`). */
    private readonly smsIvaRate = 0.16;
    /**
     * Extras que se van a cobrar en ESTA factura. No muta `Tenant.purchasedUsers`
     * hasta que el pago quede en `pago`.
     */
    pendingPurchasedUsers = 0;
    /**
     * Legado a propósito: la liberación por fila se retiró (ADM-646 hotfix) y ya no
     * se bloquea a nadie desde el modal; el campo sigue en el contrato de
     * api/api-admin (pago, tarjeta y Débito Inmediato) y viaja vacío.
     */
    pendingBlockUserIds: string[] = [];
    /** ADM-790: interruptor de la venta de cupos. Solo los specs lo cambian, para cubrir la rama `true`. */
    extraUsersSalesEnabled = EXTRA_USERS_SALES_ENABLED;
    /** Lista de planes del paso 1 desplegada («Cambiar» en la tarjeta «Tu plan»). */
    showPlanList = false;
    /** El reporte entró pero FINA debe confirmarlo a mano: paso 3 «Pago en revisión», sin confeti. */
    paymentUnderReview = false;

    get showSmsSection(): boolean {
        return this.modalMode === 'change-plan' || this.modalMode === 'buy-sms';
    }

    /**
     * Tabs "Cambiar plan" / "Comprar SMS": solo si el plan está en `pago` y no hay
     * cambio mid-cycle pendiente. En Procesando/pendiente el CTA de SMS abre solo SMS.
     */
    get showPlanSmsTabs(): boolean {
        return this.showSmsSection && this.canChangePlanInModal();
    }

    /** Modal de compra SMS sin selector de plan (p. ej. factura en Procesando). */
    get isSmsOnlyMode(): boolean {
        return this.modalMode === 'buy-sms' && !this.canChangePlanInModal();
    }

    /** Misma regla que next-plan: solo con factura en `pago` y sin mid-cycle pendiente. */
    canChangePlanInModal(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (bundle?.hasUnresolvedMidCycleChange) {
            return false;
        }
        const billStatus = (bundle?.currentBillplan?.status || '').toLowerCase();
        return billStatus === 'pago';
    }

    /**
     * Solo al pagar la factura pendiente se pueden sumar o liberar extras, y solo
     * con `create-and-edit-users`: cambiar cupos fija lo que se cobra y bloquea
     * usuarios asociados, así que `api` lo rechaza con 403 sin ese permiso. Con la
     * venta apagada (ADM-790) nunca: no hay intención de cupos en ningún camino de cobro.
     */
    canEditExtraUsers(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (this.modalMode !== 'pay' || this.isSmsPurchaseFlow || this.isMixPurchaseFlow) {
            return false;
        }
        if (!canManageExtraUserSeats(this.userService?.user$?.value, this.extraUsersSalesEnabled)) {
            return false;
        }
        return (bundle?.currentBillplan?.status || '').toLowerCase() === 'pendiente';
    }

    private resetPendingPurchasedUsers(): void {
        const bundle = this.plansService.plansBundle$?.value;
        this.pendingPurchasedUsers = normalizePurchasedUsers(bundle?.seats?.purchasedUsers ?? bundle?.purchasedUsers);
        this.pendingBlockUserIds = [];
    }

    /**
     * Mínimo de cupos a cobrar: un cupo que ocupa un usuario no se quita (primero se
     * elimina el usuario). Mismo cálculo que el «−» de `fina-extra-users-seats`.
     */
    getMinPendingPurchasedUsers(bundle?: PlansBundle): number {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        return minPurchasedUsersForOccupancy({
            currentPurchased: this.getCurrentPurchasedUsers(bundle),
            includedSeats: this.getSelectedIncludedSeats(bundle),
            occupiedSeats: bundle?.seats?.createdUsers,
        });
    }

    /**
     * Cupos incluidos con el plan ELEGIDO (+ `extendUsers`): con ellos mide api-admin la
     * ocupación al acreditar (`includedSeats(tenant.plan…)`, y `POST /plans/pay` ya escribió
     * el plan nuevo). Con el plan vigente, `seats.included` tal cual.
     */
    getSelectedIncludedSeats(bundle?: PlansBundle): number {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        const current = Math.max(0, Math.floor(Number(bundle?.seats?.included) || 0));
        if (!bundle || this.isCurrentPlanSelected(bundle)) {
            return current;
        }
        const users = this.getPlanIncludedUsers(this.getSelectedPlan(bundle), bundle);
        if (users == null) {
            return current;
        }
        return users + Math.max(0, Math.floor(Number(bundle.seats?.extendUsers) || 0));
    }

    /** Al cambiar de plan el mínimo cambia: los pedidos nunca quedan por debajo de los cupos ocupados. */
    private clampPendingPurchasedUsers(): void {
        if (!this.canEditExtraUsers()) {
            return;
        }
        this.pendingPurchasedUsers = Math.max(this.getMinPendingPurchasedUsers(), this.pendingPurchasedUsers);
    }

    onPendingPurchasedUsersChange(value: number): void {
        this.pendingPurchasedUsers = Math.max(this.getMinPendingPurchasedUsers(), normalizePurchasedUsers(value));
        this.syncAmountsFromSelection();
        if (this.hasValidationState()) {
            this.resetValidationState();
        }
    }

    /** Hay intención de extras (Δ de cupos o bloqueos) para el POST /plans/pay. */
    hasExtraUsersIntent(bundle?: PlansBundle): boolean {
        return this.canEditExtraUsers(bundle) && (this.getExtraUsersDeltaBaseUsd(bundle) !== 0 || (this.pendingBlockUserIds || []).length > 0);
    }

    getExtraUsersDeltaBaseUsd(bundle?: PlansBundle): number {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!this.canEditExtraUsers(bundle)) {
            return 0;
        }
        return extraUsersDeltaBaseUSD(this.getCurrentPurchasedUsers(bundle), this.pendingPurchasedUsers, this.getPaymentFrequencyForApi(), getExtraUserPriceUsd(bundle));
    }

    getExtraUsersDeltaAmountUsd(bundle?: PlansBundle): number {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!this.canEditExtraUsers(bundle)) {
            return 0;
        }
        return extraUsersDeltaAmountUSD(this.getCurrentPurchasedUsers(bundle), this.pendingPurchasedUsers, this.getPaymentFrequencyForApi(), this.isCommercePlusIva(), getExtraUserPriceUsd(bundle));
    }

    getCurrentPurchasedUsers(bundle?: PlansBundle): number {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        return normalizePurchasedUsers(bundle?.seats?.purchasedUsers ?? bundle?.purchasedUsers);
    }

    /**
     * Base USD de TODOS los cupos que cobra la factura, no solo del Δ: la
     * renovación cobra los cupos vigentes más los pedidos (ADM-743). Con cupón, la
     * parte facturada la informa api-admin (`desglose.extraUsersBaseUsd`, que ya va
     * dentro de `desglose.baseUsd`) y aquí solo se le suma el Δ de los pedidos.
     * Con la venta apagada (ADM-790) es 0, también con cupón: api-admin ya no cobra
     * cupos, así que no se suma `desglose.extraUsersBaseUsd`.
     */
    getExtraUsersBaseUsd(bundle?: PlansBundle): number {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!this.extraUsersSalesEnabled || this.isMidCyclePlanOnlyFee(bundle) || this.isSmsPurchaseFlow) {
            return 0;
        }
        const desglose = this.activeCouponDesglose;
        if (desglose) {
            return Number((Number(desglose.extraUsersBaseUsd ?? 0) + this.getExtraUsersDeltaBaseUsd(bundle)).toFixed(2));
        }
        return extraUsersBaseUSD(this.getChargedExtraSeats(bundle), this.getPaymentFrequencyForApi(), getExtraUserPriceUsd(bundle));
    }

    /** Cupos adicionales que cobra la factura: los pedidos si se pueden editar; si no, los vigentes. Con la venta apagada, 0. */
    private getChargedExtraSeats(bundle?: PlansBundle): number {
        if (!this.extraUsersSalesEnabled) {
            return 0;
        }
        return this.canEditExtraUsers(bundle) ? this.pendingPurchasedUsers : this.getCurrentPurchasedUsers(bundle);
    }

    /** Conteo de la fila «N cupos adicionales × meses» del resumen: el mismo que cobra `getExtraUsersBaseUsd`. */
    getSummaryExtraSeatsCount(bundle?: PlansBundle): number {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        return this.getChargedExtraSeats(bundle);
    }

    // ── Cupo fiscal extra (ADM-740) ──
    // Espejo de api-admin: lo facturado (claves de la factura) para el Δ y la intención; lo
    // facturado o el Tenant solo para cotizar con cambio de plan o frecuencia. Ver la regla
    // `plan-payment-contracts.md`, «Cupo fiscal extra (ADM-740)».

    /**
     * Cantidad de facturas adicionales pedida. `null` = sin tocar: vale la cantidad por
     * defecto (`getQuoteFiscalQuota`). Solo `onExtraControlNumbersChange` le asigna un número.
     */
    pendingExtraControlNumbers: number | null = null;
    /** `fiscal_offer_checkout_viewed` ya salió en esta apertura del modal. */
    private fiscalOfferViewedTracked = false;
    /** La plantilla consulta la oferta y las opciones en cada ciclo: se normalizan una vez por bundle. */
    private fiscalOfferMemo: { raw: unknown; offer: FiscalOffer } | null = null;
    private fiscalOptionsMemo: { bundle: PlansBundle | null | undefined; options: FiscalQuotaOption[] } | null = null;

    /** Única lectura de `bundle.fiscalOffer` (falla cerrado: ausente = venta cerrada). */
    private getFiscalOffer(bundle?: PlansBundle): FiscalOffer {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        const raw = bundle?.fiscalOffer;
        if (this.fiscalOfferMemo && this.fiscalOfferMemo.raw === raw) {
            return this.fiscalOfferMemo.offer;
        }
        const offer = normalizeFiscalOffer(raw);
        this.fiscalOfferMemo = { raw, offer };
        return offer;
    }

    isCurrentPlanFiscal(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        return isFiscalOfferPlan(bundle?.currentPlan, this.getFiscalOffer(bundle));
    }

    /** El plan ELEGIDO es el fiscal (el que cobra el cupo extra). */
    isFiscalDestination(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!bundle) {
            return false;
        }
        return isFiscalOfferPlan(this.getSelectedPlan(bundle), this.getFiscalOffer(bundle));
    }

    /**
     * La cantidad solo se elige al pagar la renovación (factura `pendiente`). A diferencia de
     * los cupos de usuario, no exige permiso de usuario (D19): quien paga el plan la elige.
     */
    canEditFiscalQuotaBill(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (this.modalMode !== 'pay' || this.isSmsPurchaseFlow || this.isMixPurchaseFlow) {
            return false;
        }
        return (bundle?.currentBillplan?.status || '').toLowerCase() === 'pendiente';
    }

    /** Lo que la factura cobra del extra, estricto: sin claves cobra 0 (no cae al Tenant). */
    private getBilledFiscalQuota(bundle?: PlansBundle): FiscalQuota | null {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        return readBilledFiscalQuota(bundle?.currentBillplan);
    }

    private getTenantFiscalQuota(bundle?: PlansBundle): FiscalQuota | null {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        return normalizeFiscalQuota(bundle?.currentExtraControlNumbers ?? null);
    }

    /**
     * Para COTIZAR (cambio de plan o frecuencia) y como valor por defecto del selector, espejo de
     * `resolveQuoteFiscalQuota`: con el plan actual fiscal, lo facturado o, sin claves, el cupo
     * del Tenant. Con el plan actual NO fiscal, `null` aunque la factura traiga claves residuales:
     * entrar al Plan Fiscal parte de 0.
     */
    private getQuoteFiscalQuota(bundle?: PlansBundle): FiscalQuota | null {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!this.isCurrentPlanFiscal(bundle)) {
            return null;
        }
        return this.getBilledFiscalQuota(bundle) ?? this.getTenantFiscalQuota(bundle);
    }

    /**
     * Lo que el sello del cupón incluyó del extra (`resolveSealedFiscalQuota`): las claves de la
     * factura, solo con el plan actual y el destino fiscales. Contra esto se mide el Δ con cupón
     * y el 409 de tarjeta `COUPON_FISCAL_QUOTA_CHANGE_UNSUPPORTED`.
     */
    private getSealedFiscalQuota(bundle?: PlansBundle): FiscalQuota | null {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        return this.isFiscalDestination(bundle) && this.isCurrentPlanFiscal(bundle) ? this.getBilledFiscalQuota(bundle) : null;
    }

    /**
     * Opciones del selector: 0, el catálogo y las cantidades vigentes (por defecto y Tenant).
     * Solo dependen del bundle, así que se memorizan por su referencia (cada emisión es otra).
     */
    getExtraControlNumbersOptions(bundle?: PlansBundle): FiscalQuotaOption[] {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (this.fiscalOptionsMemo && this.fiscalOptionsMemo.bundle === bundle) {
            return this.fiscalOptionsMemo.options;
        }
        const offer = this.getFiscalOffer(bundle);
        const tenant = this.isCurrentPlanFiscal(bundle) ? this.getTenantFiscalQuota(bundle) : null;
        const options = buildFiscalQuotaOptions({ tiers: offer.tiers, includedQuantity: offer.includedQuantity, keep: [this.getQuoteFiscalQuota(bundle), tenant] });
        this.fiscalOptionsMemo = { bundle, options };
        return options;
    }

    getFiscalIncludedQuantity(bundle?: PlansBundle): number | null {
        return this.getFiscalOffer(bundle).includedQuantity;
    }

    /**
     * Cantidad pedida: 0 con destino no fiscal; con destino fiscal, la elegida si sigue entre
     * las opciones vigentes (un catálogo nuevo no deja una elección vieja) o la de por defecto.
     */
    getRequestedExtraControlNumbers(bundle?: PlansBundle): number {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!this.isFiscalDestination(bundle)) {
            return 0;
        }
        const fallback = this.getQuoteFiscalQuota(bundle)?.quantity ?? 0;
        const pending = this.pendingExtraControlNumbers;
        if (pending === null) {
            return fallback;
        }
        return this.getExtraControlNumbersOptions(bundle).some(option => option.quantity === pending) ? pending : fallback;
    }

    /**
     * `extraControlNumbers` viaja solo si la cantidad pedida difiere de la facturada (sin
     * claves, 0). Salir de fiscal manda 0. Sin `fiscalOffer` (servidor sin ADM-740) nunca viaja.
     * Con la venta cerrada (ADM-828, QA integración fiscal CB-102) tampoco: el extra ya facturado
     * se cobra tal cual (`bill.amount`) y salir de fiscal lo limpia api-admin con el campo omitido.
     */
    hasExtraControlNumbersIntent(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!bundle?.fiscalOffer || !this.getFiscalOffer(bundle).available || !this.canEditFiscalQuotaBill(bundle)) {
            return false;
        }
        return this.getRequestedExtraControlNumbers(bundle) !== (this.getBilledFiscalQuota(bundle)?.quantity ?? 0);
    }

    /** Cupo pedido con su precio: congelado si es el del Tenant fiscal, si no el del tier. */
    private getRequestedFiscalQuota(bundle?: PlansBundle): FiscalQuota | null {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        return resolveRequestedFiscalQuota(this.getRequestedExtraControlNumbers(bundle), {
            tenantQuota: this.getTenantFiscalQuota(bundle),
            currentPlanIsFiscal: this.isCurrentPlanFiscal(bundle),
            tiers: this.getFiscalOffer(bundle).tiers,
        });
    }

    /** Cupo que cobra el período: el mismo que cotiza api-admin en cada caso. */
    getChargedFiscalQuota(bundle?: PlansBundle): FiscalQuota | null {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!bundle || this.isSmsPurchaseFlow || this.isMixPurchaseFlow || !this.isFiscalDestination(bundle)) {
            return null;
        }
        if (this.isMidCycleChange(bundle)) {
            return this.getTenantFiscalQuota(bundle);
        }
        if (this.hasExtraControlNumbersIntent(bundle)) {
            return this.getRequestedFiscalQuota(bundle);
        }
        if (this.hasPlanSelectionChanged(bundle) || this.hasFrequencySelectionChanged(bundle)) {
            return this.getQuoteFiscalQuota(bundle);
        }
        return this.getBilledFiscalQuota(bundle);
    }

    /**
     * Base USD del cupo fiscal COMPLETO del período, no del Δ. Con cupón, la parte facturada
     * la informa api-admin (`desglose.extraControlNumbersBaseUsd`, ya dentro de `baseUsd`) y
     * aquí solo se le suma el Δ pedido.
     */
    getExtraControlNumbersBaseUsd(bundle?: PlansBundle): number {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (this.isMidCyclePlanOnlyFee(bundle) || this.isSmsPurchaseFlow) {
            return 0;
        }
        const desglose = this.activeCouponDesglose;
        if (desglose) {
            return Number((Number(desglose.extraControlNumbersBaseUsd ?? 0) + this.getExtraControlNumbersDelta(bundle).deltaBase).toFixed(2));
        }
        return fiscalQuotaPeriodBaseUsd(this.getChargedFiscalQuota(bundle), this.getPaymentFrequencyForApi());
    }

    /**
     * Δ sobre lo que la factura ya cobra, con el modo de `computeRecurringIntentCharge`: lo
     * sellado (`sealed`) solo con cupón Y cambio de plan o frecuencia; en otro caso lo facturado
     * (`patch`), aunque haya cupón. 0 sin intención: el servidor solo lo aplica cuando el campo viaja.
     */
    getExtraControlNumbersDelta(bundle?: PlansBundle): { deltaBase: number; deltaPlus: number; deltaAmount: number } {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!this.hasExtraControlNumbersIntent(bundle)) {
            return { deltaBase: 0, deltaPlus: 0, deltaAmount: 0 };
        }
        const sealedMode = !!this.activeCouponDesglose && (this.hasPlanSelectionChanged(bundle) || this.hasFrequencySelectionChanged(bundle));
        return fiscalQuotaAmountDelta({
            current: sealedMode ? this.getSealedFiscalQuota(bundle) : this.getBilledFiscalQuota(bundle),
            next: this.getRequestedFiscalQuota(bundle),
            paymentFrequency: this.getPaymentFrequencyForApi(),
            plus: this.isCommercePlusIva(),
        });
    }

    /**
     * Selector editable: renovación, destino fiscal, venta abierta y acceso fiscal del comercio.
     * Con la venta cerrada (ADM-828, CB-102) no se ofrece ni conservar ni bajar el extra.
     */
    canEditExtraControlNumbers(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!bundle?.fiscalOffer || !this.canEditFiscalQuotaBill(bundle) || !this.isFiscalDestination(bundle)) {
            return false;
        }
        if (!this.fiscalAccess.hasFiscalAccess() || !this.getFiscalOffer(bundle).available) {
            return false;
        }
        return this.getExtraControlNumbersOptions(bundle).length > 1;
    }

    /** Opciones del selector; solo se usa con la venta abierta. */
    extraControlNumbersChoices(bundle?: PlansBundle): FiscalQuotaOption[] {
        return this.getExtraControlNumbersOptions(bundle);
    }

    /**
     * Sección «Facturación fiscal»: editable, o en solo lectura si el período cobra un extra.
     * Con la venta cerrada no se muestra nunca (CB-102): el extra que la factura ya cobra queda
     * como línea fija del total («números de control adicionales»).
     */
    showExtraControlNumbersSection(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!bundle || this.isMixPurchaseFlow || this.isSmsPurchaseFlow || !this.getFiscalOffer(bundle).available) {
            return false;
        }
        if (this.canEditExtraControlNumbers(bundle)) {
            return true;
        }
        return (this.getChargedFiscalQuota(bundle)?.quantity ?? 0) > 0;
    }

    onExtraControlNumbersChange(value: number): void {
        const bundle = this.plansService.plansBundle$?.value;
        const quantity = Number(value);
        if (!this.canEditExtraControlNumbers(bundle) || !this.extraControlNumbersChoices(bundle).some(option => option.quantity === quantity)) {
            return;
        }
        const previous = this.getRequestedExtraControlNumbers(bundle);
        this.pendingExtraControlNumbers = quantity;
        this.syncAmountsFromSelection();
        if (this.hasValidationState()) {
            this.resetValidationState();
        }
        if (previous !== quantity) {
            this.trackFiscalOfferSelected(previous, quantity);
        }
    }

    private fiscalOfferCheckoutProps(extra: { previous: number | null; errorCode?: string | null; channel?: FiscalOfferCheckoutChannel | null }) {
        const bundle = this.plansService.plansBundle$?.value;
        return buildFiscalOfferCheckoutProps({
            currentPlanFiscal: this.isCurrentPlanFiscal(bundle),
            targetPlanFiscal: this.isFiscalDestination(bundle),
            extraControlNumbers: this.getRequestedExtraControlNumbers(bundle),
            previousExtraControlNumbers: extra.previous,
            paymentFrequency: this.getPaymentFrequencyForApi(),
            fiscalSalesAvailable: this.getFiscalOffer(bundle).available,
            errorCode: extra.errorCode ?? null,
            channel: extra.channel ?? null,
        });
    }

    /**
     * Una vez por apertura, con el modal abierto y el selector editable. `getPlans()` corre en
     * cada emisión del bundle aunque el modal esté cerrado: sin `activePayModalRef` no cuenta.
     */
    private trackFiscalOfferViewed(): void {
        if (this.fiscalOfferViewedTracked || !this.activePayModalRef || !this.canEditExtraControlNumbers()) {
            return;
        }
        this.fiscalOfferViewedTracked = true;
        this.analyticsService.analyticsCapture(FISCAL_OFFER_CHECKOUT_EVENTS.viewed, this.fiscalOfferCheckoutProps({ previous: this.getBilledFiscalQuota()?.quantity ?? null }));
    }

    private trackFiscalOfferSelected(previous: number, _next: number): void {
        this.analyticsService.analyticsCapture(FISCAL_OFFER_CHECKOUT_EVENTS.selected, this.fiscalOfferCheckoutProps({ previous }));
    }

    private trackFiscalOfferRemoved(previous: number): void {
        this.analyticsService.analyticsCapture(FISCAL_OFFER_CHECKOUT_EVENTS.removed, this.fiscalOfferCheckoutProps({ previous }));
    }

    /**
     * Único punto de efectos de un error del cupo fiscal (cuatro 409 o el 400 de cantidad), de
     * pago móvil (`sendPlanPayment`), tarjeta o Débito Inmediato. El toast ya lo mostró quien
     * recibió el error.
     */
    onFiscalQuotaConflict(code: FiscalQuotaIssueCode, channel: FiscalOfferCheckoutChannel): void {
        if (channel !== 'pago_movil') {
            this.settleCardChargeAttempt();
        }
        this.analyticsService.analyticsCapture(
            FISCAL_OFFER_CHECKOUT_EVENTS.blocked,
            this.fiscalOfferCheckoutProps({ previous: this.getBilledFiscalQuota()?.quantity ?? null, errorCode: code, channel }),
        );
        if (code === FIRST_PAYMENT_FISCAL_QUOTA_CHANGE_UNSUPPORTED || code === FISCAL_QUOTA_REJECTED) {
            this.pendingExtraControlNumbers = null;
        }
        if (code === FISCAL_SALES_DISABLED) {
            // Con el bundle nuevo, `getPlans()` vuelve al plan actual (el fiscal queda oculto).
            this.goBackToPlanStep();
        }
        // Los montos de `payFormData` (y la huella de validación) se alinean ya con la cantidad
        // que quedó, sin esperar al bundle: un reporte en esa ventana mandaría el Δ rechazado.
        this.syncAmountsFromSelection();
        // El catch de `payPlan$` ya refresca en todo 409; el 400 no lo refresca nadie más.
        if (code === FISCAL_QUOTA_REJECTED || channel !== 'pago_movil') {
            this.plansService.obtainPlansBundle();
        }
    }

    /** Selector de cupos del paso 1: con la venta apagada (ADM-790) no se muestra. */
    showExtraUsersSeats(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!this.extraUsersSalesEnabled) {
            return false;
        }
        return this.canEditExtraUsers(bundle) || (!this.isMixPurchaseFlow && this.getCurrentPurchasedUsers(bundle) > 0);
    }

    /** Usuarios del plan en el paso 1: con la venta apagada (ADM-790) son ilimitados; si no, «Incluye N usuarios». */
    planUsersLabel(plan: Plan | null | undefined, bundle?: PlansBundle): string | null {
        if (!this.extraUsersSalesEnabled) {
            return 'Usuarios ilimitados';
        }
        const users = this.getPlanIncludedUsers(plan, bundle);
        if (!users) {
            return null;
        }
        return `Incluye ${users} ${users === 1 ? 'usuario' : 'usuarios'}`;
    }

    readonly paymentFrequencyOptions: { id: PayPlanFrequencyId; label: string; savings: string | null }[] = [
        { id: 'monthly', label: 'Mensual', savings: null },
        { id: 'quarterly', label: 'Trimestral', savings: 'Ahorra 10%' },
        { id: 'semiannual', label: 'Semestral', savings: 'Ahorra 20%' },
        { id: 'yearly', label: 'Anual', savings: 'Ahorra 30%' },
    ];

    private readonly frequencyRank: Record<PayPlanFrequencyId, number> = {
        monthly: 1,
        quarterly: 2,
        semiannual: 3,
        yearly: 4,
    };

    /** Frecuencias ofrecidas: solo igual o superior a la del tenant. */
    get selectablePaymentFrequencyOptions(): { id: PayPlanFrequencyId; label: string; savings: string | null }[] {
        const currentId = this.mapBundlePaymentFrequencyToUi();
        const minRank = this.frequencyRank[currentId] ?? 1;
        return this.paymentFrequencyOptions.filter(option => (this.frequencyRank[option.id] ?? 0) >= minRank);
    }

    constructor(
        public modalService: NgbModal,
        public plansService: PlansService,
        private toastService: ToastService,
        private calendar: NgbCalendar,
        public formatter: NgbDateParserFormatter,
        public commerceService: CommerceService,
        private paymentPlanModalRequestService: PaymentPlanModalRequestService,
        public analyticsService: AnalyticsService,
        private smsService: SmsService,
        private userService: UserService,
        private fiscalAccess: FiscalAccessService,
        private controlNumbersStatus: ControlNumbersStatusService,
    ) {
        this.resetPaymentDateToToday();
    }

    ngOnInit() {
        this.plansService.plansBundle$.pipe(takeUntil(this.destroy$)).subscribe(bundle => {
            if (bundle?.currentBillplan) {
                this.getPlans();
                this.loadingPlans = false;
            }
        });
        this.commerceService.exchangeRateBCV$.pipe(takeUntil(this.destroy$)).subscribe(rate => {
            // Mix cotizado con la tasa de otro día (el del pago reportado): la de hoy no la pisa.
            if (this.isMixPurchaseFlow && this.mixDayRate?.rate != null) {
                return;
            }
            const exchangeRateBCV = Number(Number(rate || 0).toFixed(2));
            // En el mix `payFormData.exchangeRateBCV` guarda la tasa cruda: se compara cruda contra cruda.
            const currentRate = Number(this.payFormData.exchangeRateBCV || 0);
            const unchanged = this.isMixPurchaseFlow ? Number(rate || 0) === currentRate : exchangeRateBCV === currentRate;
            if (!exchangeRateBCV || unchanged) {
                return;
            }

            const shouldInvalidate = this.hasValidationState();
            // Mix: tasa cruda (api-admin compara el VES contra la oficial sin redondear).
            this.payFormData = { ...this.payFormData, exchangeRateBCV: this.isMixPurchaseFlow ? Number(rate) : exchangeRateBCV };
            if (this.isMixPurchaseFlow) {
                this.syncMixAmountsFromCart();
            } else if (this.isSmsPurchaseFlow && this.selectedSmsPackage) {
                this.syncSmsAmountsFromSelection();
            } else if (this.plansService.plansBundle$?.value?.currentBillplan) {
                this.syncAmountsFromSelection();
            }
            if (shouldInvalidate) {
                this.resetValidationState();
            }
        });
        // Tasa BCV del mix por banco: la del día del pago reportado, acotado a [ayer, hoy] en
        // Caracas como api-admin (`resolveMixBankQuoteDay`). Hoy recarga la tasa de hoy; un día
        // anterior pide la oficial de ese día. `switchMap` descarta la respuesta de una fecha vieja.
        this.mixRateDay$
            .pipe(
                tap(() => (this.refreshingMixRate = true)),
                switchMap(day => {
                    const quoteDay = resolveMixBankQuoteDay(day, this.clockNow());
                    const rate$ = quoteDay ? this.commerceService.getExchangeRateBCVByDate$(quoteDay) : this.commerceService.refreshExchangeRateBCV$();
                    return rate$.pipe(map(rate => ({ quoteDay, rate })));
                }),
                takeUntil(this.destroy$),
            )
            .subscribe(({ quoteDay, rate }) => this.applyMixDayRate(quoteDay, rate));
        this.getPaymentMethods();
        // Pipeline único para consultar el estado de la tarjeta. `switchMap` cancela
        // cualquier request en vuelo cuando llega uno nuevo (warm-up + cada apertura
        // de la modal), evitando que una respuesta vieja sobrescriba `hasSavedCard`.
        this.loadCard$
            .pipe(
                switchMap(() => {
                    if (!this.isCardMethodEligible()) {
                        return of<{ hasCard: boolean } | null>(null);
                    }
                    return this.plansService.getCard$().pipe(
                        // Preseleccionamos "Tarjeta en dólares" con que exista una tarjeta
                        // guardada, sin exigir `enabled`: si la tarjeta fue rechazada y el
                        // backend llegara a apagar el cobro automático, igual queremos llevar
                        // al usuario al flujo de reemplazo (que es lo que promete el CTA
                        // "Actualizar tarjeta"). Hoy `hasCard` y `enabled` van juntos, así
                        // que esto no cambia el comportamiento actual.
                        map((res: any) => ({ hasCard: Boolean(res?.data?.hasCard) })),
                        catchError(() => of({ hasCard: false })),
                    );
                }),
                takeUntil(this.destroy$),
            )
            .subscribe(result => {
                this.hasSavedCard = result ? result.hasCard : false;
                // Si ya estamos en el paso 2 sin método elegido, preseleccionar tarjeta.
                if (this.hasSavedCard && this.canUseCardForSelection() && this.payModalStep === 2 && !this.cardMethodSelected && !this.pagoMovilSelected) {
                    this.selectCardMethod();
                }
            });
        // Warm-up: resolvemos el estado de la tarjeta al cargar el componente para
        // que `hasSavedCard` ya tenga su valor definitivo cuando el usuario abra la
        // modal y avance al paso 2 (evita la carrera con el HTTP en vuelo).
        this.loadSavedCardState();
        // Validación en vivo del cupón. `switchMap` descarta la respuesta de un
        // código que el usuario ya cambió; `catchError` dentro del inner observable
        // mantiene vivo el pipeline tras un rechazo (400/404/409 son esperables).
        this.couponCodeTyped$
            .pipe(
                debounceTime(COUPON_PREVIEW_DEBOUNCE_MS),
                // Se descarta el código repetido solo si ya está validado y el cobro
                // no está bloqueado: tras un rechazo o un apply fallido, reintentar
                // el mismo código tiene que volver a pedir el preview.
                distinctUntilChanged((previous, current) => previous === current && !!this.appliedCoupon && !this.couponPaymentBlocked),
                switchMap(code => {
                    const billPlanId = this.plansService.plansBundle$?.value?.currentBillplan?._id;
                    // La selección se resuelve al salir el request (no al teclear):
                    // así el desglose corresponde a lo que el paso 1 muestra ahora.
                    const selectionKey = this.getCouponSelectionKey();
                    if (!code || !billPlanId) {
                        return of({ code, selectionKey, response: null as CouponPreviewResponse | null, error: null as any });
                    }
                    return this.plansService.previewCoupon$(billPlanId, code, this.getCouponSelection()).pipe(
                        map(response => ({ code, selectionKey, response, error: null as any })),
                        catchError(error => of({ code, selectionKey, response: null as CouponPreviewResponse | null, error })),
                    );
                }),
                takeUntil(this.destroy$),
            )
            .subscribe(({ code, selectionKey, response, error }) => this.handleCouponPreview(code, selectionKey, response, error));
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.loadCard$.complete();
        this.couponCodeTyped$.complete();
        this.clearCouponHoldTimer();

        // Clean up validation timer to prevent memory leaks
        if (this.validationTimerInterval) {
            clearInterval(this.validationTimerInterval);
            this.validationTimerInterval = null;
        }
        this.destroyPayPlanConfetti();
    }

    ngAfterViewInit() {
        this.paymentPlanModalRequestService.openRequested$.pipe(takeUntil(this.destroy$)).subscribe(request => {
            // Sin datos = `'pay'`: es lo que siempre pidió el banner de renovación (y lo que emiten
            // los mocks de los specs), así que su comportamiento no cambia.
            const mode = request?.mode ?? 'pay';
            // Un cambio de plan es cosa de la página de planes. Las instancias del layout (modales de
            // cuenta vencida o desactivada) viven en `ng-template` y normalmente no están montadas,
            // pero si lo estuvieran no deben abrir un segundo modal encima.
            if (mode !== 'pay' && !this.isPlan) return;
            // Mismas opciones que usa cada flujo hoy: 'pay' abre en `md` (banner de renovación) y
            // 'change-plan' en el `lg` por defecto (como `next-plan`).
            this.openWithMode(mode, mode === 'pay' ? { size: 'md', centered: true, backdrop: 'static', keyboard: false } : undefined);
            if (request?.preselectNextPlan) this.preselectNextPlan();
        });
    }

    /**
     * Abre el modal según el CTA: pay (sin SMS), change-plan (con SMS), buy-sms (tab SMS).
     * Pay y change-plan llevan `fpp-pay-modal--plan` (700px, diseño «Confirmar pago»):
     * el ancho se fija al abrir y vale para toda la ventana, pestaña SMS y pasos 2-3 incluidos.
     */
    openWithMode(mode: PayPlanModalMode, modalOptions?: NgbModalOptions): void {
        this.modalMode = mode;
        this.activePlanSmsTab = mode === 'buy-sms' ? 'sms' : 'plan';
        const options = modalOptions ?? { size: 'lg', centered: true, backdrop: 'static', keyboard: false };
        const planWindowClass = mode === 'buy-sms' ? '' : 'fpp-pay-modal--plan';
        this.open(this.payData, { ...options, windowClass: [options.windowClass, planWindowClass].filter(Boolean).join(' ') || undefined });
    }

    openMix(cart: MixPayCart): void {
        this.modalMode = 'mix';
        this.mixCart = cart;
        this.mixCheckoutSettled = false;
        this.mixDayRate = null;
        this.isSmsPurchaseFlow = false;
        this.resetPaymentDateToToday();
        // `payFormData` puede traer la tasa redondeada de un flujo anterior (plan/SMS): el mix usa la cruda.
        const rawRate = Number(this.commerceService.exchangeRateBCV$?.getValue());
        if (rawRate > 0) {
            this.payFormData = { ...this.payFormData, exchangeRateBCV: rawRate };
        }
        this.syncMixAmountsFromCart();
        this.open(this.payData, { size: 'lg', centered: true, backdrop: 'static', keyboard: false });
        this.syncMixAmountsFromCart();
        this.payModalStep = 2;
        // Igual que el pago de plan (`enterPaymentStep`): tarjeta guardada o el comercio elige.
        // Preseleccionar `paymentMethods[0]` dejaba el pago móvil sin la verificación BNC.
        if (this.hasSavedCard && this.canUseCardForSelection()) {
            this.showAllMethods = false;
            this.selectCardMethod();
        } else {
            this.showAllMethods = true;
        }
        // Recarga la tasa ANTES de que el cliente vea el VES a transferir (la de hoy: la fecha
        // arranca en hoy). No se recarga antes del POST: al reportar, la transferencia ya ocurrió.
        this.mixRateDay$.next(this.toIsoDate(this.fromDate));
    }

    /**
     * Resultado del pipeline de `mixRateDay$`. Con la tasa del día pedido, esa; si el día es hoy
     * o su tasa no cargó, la de hoy en memoria (la subject, que `refreshExchangeRateBCV$` ya
     * actualizó si respondió). Re-sincroniza directo, sin depender de la suscripción a
     * `exchangeRateBCV$`.
     */
    private applyMixDayRate(quoteDay: string | null, rate: number | null): void {
        this.refreshingMixRate = false;
        if (!this.isMixPurchaseFlow) {
            return;
        }
        this.mixDayRate = quoteDay ? { day: quoteDay, rate: rate || null } : null;
        if (this.mixDayRate?.rate == null) {
            // Tasa de hoy: la recargada o, si falló (o el día pedido no cargó), la de la subject.
            // `payFormData` es solo salida del sync: sin esto, volver a hoy dejaría la tasa de ayer.
            const todayRate = (!quoteDay && rate) || Number(this.commerceService.exchangeRateBCV$?.getValue()) || Number(this.payFormData.exchangeRateBCV);
            this.payFormData = { ...this.payFormData, exchangeRateBCV: todayRate };
        }
        const previousVES = this.payFormData.amountVES;
        this.syncMixAmountsFromCart();
        if (this.payFormData.amountVES !== previousVES && this.hasValidationState()) {
            this.resetValidationState();
        }
    }

    /** Reloj del componente: los specs lo fijan para que «hoy» coincida con el del calendario. */
    protected clockNow(): Date {
        return new Date();
    }

    /**
     * Instante con el que se cotizan los meses de cupos del mix. Con tarjeta, el reloj (cobra en
     * el acto). Por banco, el día del pago reportado (`fromDate`, el mismo `paidDate` del body)
     * acotado a [ayer, hoy], como `resolveMixBankQuoteInstant` de api-admin.
     */
    private mixQuoteNow(): Date {
        const now = this.clockNow();
        return this.cardMethodSelected ? now : mixBankQuoteInstant(this.toIsoDate(this.fromDate), now);
    }

    /** Día de la tasa del mix en 'dd/MM', partiendo el string: sin pipe `date` (correría el día en UTC+). */
    get mixRateDayLabel(): string {
        const [, month, day] = (this.mixDayRate?.day || '').split('-');
        return day && month ? `${day}/${month}` : '';
    }

    /** Desglose del paso 2 del mix: las mismas `lines` que el aside de «Mejora tu Fina». */
    get mixSummaryLines(): MixQuoteLine[] {
        return this.isMixPurchaseFlow ? this.mixQuote?.lines ?? [] : [];
    }

    /**
     * Líneas del carrito mix para el banner «Escogiste». Sale del carrito (no de la cotización)
     * pero con las unidades de `mixLineUnit` y en el orden de las `lines`: sms, números de
     * control, usuarios, inducción.
     */
    getMixPurchaseItems(): string[] {
        const cart = this.mixCart;
        if (!cart) {
            return [];
        }
        const counted: Array<Pick<MixQuoteLine, 'type' | 'quantity'>> = [
            { type: 'sms', quantity: Math.max(0, Math.floor(Number(cart.smsQuantity) || 0)) },
            { type: 'controlNumbers', quantity: Math.max(0, Math.floor(Number(cart.controlNumbersQuantity) || 0)) },
            { type: 'users', quantity: normalizePurchasedUsers(cart.extraUsersToAdd) },
        ];
        const items = counted.filter(line => line.quantity > 0).map(line => `${line.quantity.toLocaleString('es-VE')} ${mixLineUnit(line)}`);
        if (cart.includeSetup) {
            items.push(MIX_LINE_LABELS.setup);
        }
        return items;
    }

    get isMixPurchaseFlow(): boolean {
        return this.modalMode === 'mix';
    }

    setPlanSmsTab(tab: 'plan' | 'sms'): void {
        if (!this.showSmsSection) {
            return;
        }
        if (tab === 'plan' && !this.canChangePlanInModal()) {
            return;
        }
        this.activePlanSmsTab = tab;
        // Al volver del paso de pago, los montos pueden quedar del otro flujo (SMS vs plan).
        if (tab === 'plan') {
            this.isSmsPurchaseFlow = false;
            this.syncAmountsFromSelection();
        } else if (this.selectedSmsPackage) {
            this.syncSmsAmountsFromSelection();
        }
    }

    onSmsPackageSelected(pkg: SmsPackageOption): void {
        const previous = this.selectedSmsPackage;
        if (this.hasValidationState() && (previous?.id !== pkg?.id || previous?.quantity !== pkg?.quantity || previous?.priceUsd !== pkg?.priceUsd)) {
            this.resetValidationState();
        }
        this.selectedSmsPackage = pkg;
    }

    /** Compra de SMS: avanza al paso de métodos de pago. */
    proceedSmsPurchase(): void {
        if (!this.selectedSmsPackage?.quantity || !this.selectedSmsPackage?.priceUsd) {
            this.toastService.error('Paquete requerido', 'Selecciona un paquete de SMS para continuar.');
            return;
        }

        this.isSmsPurchaseFlow = true;
        this.resetPaymentDateToToday();
        this.syncSmsAmountsFromSelection();
        // Misma UX que el pago de plan: si hay tarjeta guardada, preseleccionarla.
        if (this.hasSavedCard && this.canUseCardForSelection()) {
            this.showAllMethods = false;
            this.selectCardMethod();
        } else if (this.paymentMethods.length > 0) {
            this.showAllMethods = true;
            this.onChangePayment(this.paymentMethods[0]._id, 0);
        } else {
            this.showAllMethods = true;
            this.cardMethodSelected = false;
            this.pagoMovilSelected = false;
            this.debitoInmediatoSelected = false;
            this.indexSelected = -1;
            this.paymentMethodSelected = null;
            this.payFormData.paymentMethod = '';
            this.resetValidationState();
        }
        this.payModalStep = 2;
    }

    /** Recalcula montos del formulario según el paquete SMS seleccionado. */
    syncSmsAmountsFromSelection(): void {
        const pkg = this.selectedSmsPackage;
        if (!pkg) {
            return;
        }

        const exchangeRateBCV = Number((this.payFormData.exchangeRateBCV || this.commerceService.exchangeRateBCV$?.getValue() || 0).toFixed(2)) || 0;
        const subtotalUsd = Number(pkg.priceUsd ?? 0);
        const ivaUsd = Number((subtotalUsd * this.smsIvaRate).toFixed(2));
        const amountUsd = Number((subtotalUsd + ivaUsd).toFixed(2));
        const amountVES = Number((amountUsd * exchangeRateBCV).toFixed(2));

        this.payFormData = {
            ...this.payFormData,
            amount: amountUsd,
            amountVES,
            amountPlan: subtotalUsd,
            exchangeRateBCV,
            plus: ivaUsd,
            paymentDate: this.toIsoDate(this.fromDate),
            paidDate: this.toIsoDate(this.fromDate),
        };
    }

    getSmsSubtotalUsd(): number {
        if (this.isMixPurchaseFlow) {
            return Number(this.mixCart?.subtotalUsd ?? 0);
        }
        return Number(this.selectedSmsPackage?.priceUsd ?? 0);
    }

    getSmsIvaUsd(): number {
        if (this.isMixPurchaseFlow) {
            return Number(this.mixCart?.ivaUsd ?? 0);
        }
        return Number((this.getSmsSubtotalUsd() * this.smsIvaRate).toFixed(2));
    }

    getMixRetentionUsd(): number {
        return Number(this.mixCart?.retentionUsd ?? 0);
    }

    getSmsPurchaseTitle(): string {
        const pkg = this.selectedSmsPackage;
        if (!pkg) {
            return 'Paquete SMS';
        }
        return pkg.title || `${pkg.quantity} SMS`;
    }

    getSmsPurchaseSubtitle(): string {
        const qty = this.selectedSmsPackage?.quantity;
        if (!qty) {
            return 'Compra de mensajes de texto.';
        }
        return `${qty.toLocaleString('es-VE')} mensajes de texto.`;
    }

    getPlans() {
        const bundle = this.plansService.plansBundle$?.value;
        if (!bundle?.currentBillplan) {
            return;
        }
        const rawRate = Number(this.commerceService.exchangeRateBCV$?.getValue()) || 0;
        // Mix: tasa cruda (ver `syncMixAmountsFromCart`); plan y SMS siguen redondeando a 2 decimales.
        const exchangeRateBCV = this.isMixPurchaseFlow ? rawRate : Number(rawRate.toFixed(2)) || 0;
        this.plans = this.getSelectablePlans(bundle);
        if (!this.selectedPlanId) {
            this.setSelectedPlanToCurrent(bundle);
        }
        this.payFormData = {
            ...this.payFormData,
            billPlanId: bundle.currentBillplan._id,
            exchangeRateBCV,
            paymentDate: this.toIsoDate(this.fromDate),
            paidDate: this.toIsoDate(this.fromDate),
        };
        this.paymentDateInput = this.formatter.format(this.fromDate);
        if (this.isMixPurchaseFlow) {
            this.syncMixAmountsFromCart();
        } else {
            this.syncAmountsFromSelection();
        }
        this.trackFiscalOfferViewed();
    }

    /** Meses facturados según la frecuencia elegida (alineado con badges del diseño). */
    getMonthsForFrequency(): number {
        switch (this.selectedPaymentFrequency) {
            case 'quarterly':
                return 3;
            case 'semiannual':
                return 6;
            case 'yearly':
                return 12;
            default:
                return 1;
        }
    }

    /** Valor de `paymentFrequency` esperado por el API (alineado con form-change-plan / commerce). */
    getPaymentFrequencyForApi(): string {
        switch (this.selectedPaymentFrequency) {
            case 'quarterly':
                return 'trimestral';
            case 'semiannual':
                return 'semestral';
            case 'yearly':
                return 'anual';
            default:
                return 'mensual';
        }
    }

    /** % de descuento por frecuencia (Mensual 0, Trimestral 10%, Semestral 20%, Anual 30%). */
    getFrequencyDiscountPercent(): number {
        return this.getFrequencyDiscountPercentFor(this.selectedPaymentFrequency);
    }

    getFrequencyDiscountPercentFor(id: PayPlanFrequencyId): number {
        switch (id) {
            case 'quarterly':
                return 10;
            case 'semiannual':
                return 20;
            case 'yearly':
                return 30;
            default:
                return 0;
        }
    }

    getPlanCardGrossUsd(monthlyAmount: number | null | undefined, monthlyDiscount: number | null | undefined = 0): number {
        const baseMonthly = Number(monthlyAmount ?? 0);
        const planMonthlyDiscount = this.selectedPaymentFrequency === 'monthly' ? Number(monthlyDiscount ?? 0) : 0;
        return Number(((baseMonthly + planMonthlyDiscount) * this.getMonthsForFrequency()).toFixed(2));
    }

    getPlanCardDiscountedUsd(monthlyAmount: number | null | undefined): number {
        const grossUsd = Number((Number(monthlyAmount ?? 0) * this.getMonthsForFrequency()).toFixed(2));
        const discountPercent = this.getFrequencyDiscountPercent();
        if (discountPercent <= 0) {
            return grossUsd;
        }
        return Number((grossUsd - (grossUsd * discountPercent) / 100).toFixed(2));
    }

    shouldShowPlanCardStrike(monthlyAmount: number | null | undefined, monthlyDiscount: number | null | undefined = 0): boolean {
        return this.getFrequencyDiscountPercent() > 0 || Number(monthlyDiscount ?? 0) > 0;
    }

    /** Alinea la frecuencia del paso 1 con la del tenant (`PlansBundle.paymentFrequency`). */
    private mapBundlePaymentFrequencyToUi(): PayPlanFrequencyId {
        return this.mapApiFrequencyToUi(this.plansService.plansBundle$?.value?.paymentFrequency);
    }

    private mapApiFrequencyToUi(raw: string | null | undefined): PayPlanFrequencyId {
        switch ((raw || 'mensual').toString().toLowerCase().trim()) {
            case 'trimestral':
                return 'quarterly';
            case 'semestral':
                return 'semiannual';
            case 'anual':
                return 'yearly';
            default:
                return 'monthly';
        }
    }

    /** Precio por mes del plan elegido con el descuento de esa frecuencia. Solo para mostrar en las tarjetas. */
    getFrequencyMonthlyPriceUsd(bundle: PlansBundle, id: PayPlanFrequencyId): number {
        const monthly = this.getMonthlyBaseUsd(bundle);
        return Number((monthly - (monthly * this.getFrequencyDiscountPercentFor(id)) / 100).toFixed(2));
    }

    getSelectedFrequencyLabel(): string {
        return this.paymentFrequencyOptions.find(option => option.id === this.selectedPaymentFrequency)?.label ?? 'Mensual';
    }

    /**
     * Usuarios que incluye un plan. Plan vigente: `seats.planUsers` (lo resuelve `api`).
     * Otro plan: `planIncludedUsers`, espejo de `getPlanLimitUsers`. Null = sin plan.
     */
    getPlanIncludedUsers(plan: Plan | null | undefined, bundle: PlansBundle): number | null {
        if (!plan) {
            return null;
        }
        const planUsers = Number(bundle?.seats?.planUsers);
        if (this.isCurrentPlan(plan, bundle) && Number.isFinite(planUsers) && planUsers > 0) {
            return planUsers;
        }
        return planIncludedUsers(plan);
    }

    togglePlanList(): void {
        this.showPlanList = !this.showPlanList;
    }

    /** Elige un plan desde la lista desplegable y la cierra (diseño «Confirmar pago»). */
    pickPlanFromList(planId: string): void {
        this.selectPlan(planId);
        this.showPlanList = false;
    }

    /** Precio mensual en USD (API/catálogo: plan seleccionado; factura: `amountPlan` como respaldo). */
    getMonthlyBaseUsd(bundle: PlansBundle): number {
        if (!bundle?.currentBillplan) {
            return 0;
        }
        if (this.isCurrentPlanSelected(bundle)) {
            const monthly = bundle.currentPlan?.amount ?? bundle.currentBillplan.amountPlan ?? 0;
            return Number(Number(monthly).toFixed(2));
        }
        const plan = this.findSelectedPlan(bundle);
        if (plan) {
            return Number((plan.amount ?? 0).toFixed(2));
        }
        const monthly = bundle.currentPlan?.amount ?? bundle.currentBillplan.amountPlan ?? 0;
        return Number(Number(monthly).toFixed(2));
    }

    /** Descuento en USD solo por frecuencia: mismo subtotal que muestra el resumen × % (evita desajustes con otras rutas). */
    getFrequencyDiscountUsd(bundle: PlansBundle): number {
        if (this.isMidCyclePlanOnlyFee(bundle)) {
            return 0;
        }
        const months = this.getMonthsForFrequency();
        const pct = this.getFrequencyDiscountPercent();
        if (months <= 1 || pct <= 0) {
            return 0;
        }
        const subtotalUsd = this.getSummarySubtotalUsd(bundle);
        if (subtotalUsd <= 0) {
            return 0;
        }
        return Number(((subtotalUsd * pct) / 100).toFixed(2));
    }

    /** Descuento por saldo a favor en la factura (USD), aparte del % por frecuencia. */
    getCreditDiscountUsd(bundle: PlansBundle): number {
        const desglose = this.activeCouponDesglose;
        if (desglose) {
            return Number(desglose.creditDiscountUsd ?? 0);
        }
        if (!bundle?.currentBillplan) {
            return 0;
        }
        // Mid-cycle: el fee o monto nuevo no aplica saldo a favor de la factura ya pagada.
        if (this.isMidCycleChange(bundle)) {
            return 0;
        }
        return Number((bundle.currentBillplan.discountByCredit ?? 0).toFixed(2));
    }

    /**
     * Desglose del servidor cuando hay un cupón validado; `null` en el flujo normal.
     * Es el único punto por el que el desglose de api-admin entra al resumen: los
     * getters de montos lo consultan primero y, si existe, devuelven sus campos tal
     * cual. Sin cupón, todo el cálculo queda exactamente como antes de ADM-654.
     */
    private get activeCouponDesglose(): CouponDesglose | null {
        return this.appliedCoupon && this.couponDesglose ? this.couponDesglose : null;
    }

    /** Descuento del cupón en USD. Lo calcula api-admin; aquí solo se pinta. */
    getCouponDiscountUsd(): number {
        return Number(this.activeCouponDesglose?.couponDiscountUsd ?? 0);
    }

    /**
     * Retención de IVA en USD. Solo la informa el desglose del servidor: el cálculo
     * cliente del resumen nunca la ha contemplado, así que sin cupón sigue en 0.
     */
    getSummaryRetentionUsd(): number {
        return Number(this.activeCouponDesglose?.retentionUsd ?? 0);
    }

    /** El panel se mantiene abierto mientras haya un cupón aplicado (no se puede "colapsar" un descuento). */
    get isCouponPanelOpen(): boolean {
        return this.couponPanelOpen || !!this.appliedCoupon;
    }

    openCouponPanel(): void {
        this.couponPanelOpen = true;
    }

    /**
     * El campo de cupón se ofrece con la factura vigente en `pendiente` (mismo
     * gate que el CTA "Pagar ahora"), con cualquier selección de plan y frecuencia.
     * `preview` y `apply` mandan esa selección (`getCouponSelection`) para validar
     * y calcular el desglose contra lo que se va a cobrar, no contra la factura.
     */
    canRedeemCoupon(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        // El mix no paga la factura del plan: un cupón sellado en ella no le aplica.
        if (!bundle?.currentBillplan || this.isSmsPurchaseFlow || this.isMixPurchaseFlow) {
            return false;
        }
        return (bundle.currentBillplan.status || '').toLowerCase() === 'pendiente';
    }

    /**
     * Plan y frecuencia elegidos en el paso 1, que api-admin usa para validar el
     * cupón y calcular la base del período.
     *
     * Se mandan **siempre**, coincidan o no con la factura: son los mismos valores
     * que después viajan en el cobro (`planType` / `paymentFrequency`), así que el
     * par sellado y el par cobrado no pueden divergir. Con la selección igual a la
     * de la factura, api-admin resuelve exactamente lo mismo que sin los campos.
     */
    getCouponSelection(): PlanCouponSelection {
        return {
            planType: this.selectedPlanId || null,
            paymentFrequency: this.getPaymentFrequencyForApi(),
        };
    }

    /** Huella de la selección, para descartar respuestas de una selección anterior. */
    private getCouponSelectionKey(): string {
        return `${this.selectedPlanId || ''}|${this.getPaymentFrequencyForApi()}`;
    }

    /**
     * Misma normalización que api-admin (`formatCouponCode`): trim + minúsculas.
     * Sin esto, tipar `BANCRECER20` dejaba el preview válido pero el CTA bloqueado
     * porque `hasUnresolvedCouponInput` comparaba contra `bancrecer20` de la DB.
     */
    private normalizeCouponCode(code: string | null | undefined): string {
        return String(code ?? '')
            .trim()
            .toLowerCase();
    }

    private couponCodesMatch(a: string | null | undefined, b: string | null | undefined): boolean {
        const left = this.normalizeCouponCode(a);
        const right = this.normalizeCouponCode(b);
        return !!left && left === right;
    }

    /** Cada tecla dispara el preview con debounce; el rechazo anterior deja de aplicar. */
    onCouponCodeInput(value: string): void {
        // Sellado = el canje ya está en la factura. Cambiar el código en pantalla
        // no puede retirarlo; el input va `readonly` y aquí se ignora.
        if (this.isCouponRemovalBlocked) {
            return;
        }
        const code = this.normalizeCouponCode(value);
        this.couponCodeInput = code;
        this.couponError = '';
        if (!code) {
            this.clearAppliedCoupon();
        } else if (this.appliedCoupon && !this.couponCodesMatch(this.appliedCoupon.code, code)) {
            // El desglose del código anterior no puede quedarse pintado mientras
            // el preview del nuevo está en debounce: Proceder sellaría el viejo.
            this.clearAppliedCoupon();
        }
        this.isValidatingCoupon = !!code;
        this.couponCodeTyped$.next(code);
    }

    /**
     * Resultado del preview. Un rechazo (400/404/409) llega por `error` y su `message`
     * se muestra tal cual: es copy público de api-admin. Ningún rechazo consume el uso
     * del cupón ni toca el total.
     */
    private handleCouponPreview(code: string, selectionKey: string, response: CouponPreviewResponse | null, error: any): void {
        // Respuesta obsoleta: el usuario ya cambió el código o la selección, o la
        // factura dejó de admitir cupón. El desglose de una selección anterior
        // pintaría un total que no es el que se va a cobrar. No se toca
        // `isValidatingCoupon`: el preview de la selección nueva puede estar
        // todavía en debounce y el spinner tiene que seguir.
        if (code !== this.couponCodeInput || selectionKey !== this.getCouponSelectionKey() || !this.canRedeemCoupon()) {
            return;
        }
        this.isValidatingCoupon = false;
        const hadCoupon = !!this.appliedCoupon;

        if (!this.couponCodeInput) {
            this.clearAppliedCoupon();
            if (hadCoupon) {
                this.afterCouponAmountsChanged();
            }
            return;
        }

        if (error) {
            this.couponError = error?.error?.message || COUPON_PREVIEW_FALLBACK_MESSAGE;
            this.clearAppliedCoupon();
            this.afterCouponAmountsChanged();
            return;
        }

        const data = response?.data;
        if (!data?.valid || !data.coupon || !data.desglose) {
            this.couponError = response?.message || COUPON_PREVIEW_FALLBACK_MESSAGE;
            this.clearAppliedCoupon();
            this.afterCouponAmountsChanged();
            return;
        }

        this.appliedCoupon = data.coupon;
        this.couponDesglose = data.desglose;
        // Alinea el input al código canónico del servidor (minúsculas) para que
        // `hasUnresolvedCouponInput` no bloquee tras un preview OK con distinta capitalización.
        this.couponCodeInput = this.normalizeCouponCode(data.coupon.code);
        this.couponError = '';
        this.couponPaymentBlocked = false;
        // Un preview nuevo no es un sello, salvo que coincida con el de esta sesión
        // o con el que ya está en la factura (`GET /plans` trae `appliedCoupon`).
        const sameAsSession =
            !!this.sealedCouponSnapshot && this.couponCodesMatch(this.sealedCouponSnapshot.code, data.coupon.code) && this.sealedCouponSnapshot.selectionKey === this.getCouponSelectionKey();
        const serverCoupon = this.getLiveServerCoupon();
        const sameAsServer = !!serverCoupon && this.couponCodesMatch(serverCoupon.code, data.coupon.code) && this.serverSealMatchesSelection(serverCoupon);
        this.couponSealed = sameAsSession || sameAsServer;
        if (this.couponSealed) {
            this.couponReservedUntil = serverCoupon?.reservedUntil ?? this.couponReservedUntil;
            this.rememberSealedSnapshot();
            this.scheduleCouponHoldExpiry(this.couponReservedUntil);
        }
        this.afterCouponAmountsChanged();
    }

    /** Con el cupón ya sellado en la factura, quitarlo necesita al backend. */
    get isCouponRemovalBlocked(): boolean {
        return !!this.sealedCouponSnapshot || !!this.getLiveServerCoupon() || (this.couponSealed && !!this.appliedCoupon);
    }

    /** "Quitar": devuelve el total al precio sin cupón sin recargar la modal. */
    removeCoupon(): void {
        if (!this.appliedCoupon && !this.couponCodeInput && !this.couponError) {
            return;
        }
        // Sellado = el descuento ya está en la factura y los caminos de cobro leen de
        // ahí. Quitarlo solo en pantalla dejaría a la UI mintiendo sobre lo que se va
        // a cobrar, y api-admin todavía no expone cómo retirar un canje (pedido en el
        // handoff ADM-654 → api-admin).
        if (this.isCouponRemovalBlocked) {
            this.couponError = COUPON_SEALED_REMOVAL_MESSAGE;
            return;
        }
        this.clearAppliedCoupon();
        this.couponCodeInput = '';
        // Si un apply falló, la factura puede seguir sellada: no se limpia el
        // mensaje ni se desbloquea el cobro al ocultar el preview.
        if (!this.couponPaymentBlocked) {
            this.couponError = '';
        }
        // El panel sigue abierto: quitar un cupón suele ser el paso previo a probar otro.
        this.couponPanelOpen = true;
        // Sin código, el pipeline no dispara request y deja de esperar respuestas viejas.
        this.couponCodeTyped$.next('');
        this.afterCouponAmountsChanged();
    }

    /** Mensaje del chip verde bajo el campo cuando el cupón queda validado. */
    getAppliedCouponMessage(): string {
        const coupon = this.appliedCoupon;
        if (!coupon) {
            return '';
        }
        if (coupon.discountType === 'specialPrice') {
            return `Código ${coupon.code} aplicado · precio especial de ${this.formatCouponAmountUsd(coupon.value)}`;
        }
        if (coupon.discountType === 'percentage') {
            return `Código ${coupon.code} aplicado · ${coupon.value}% de descuento`;
        }
        return `Código ${coupon.code} aplicado · ${this.formatCouponAmountUsd(this.getCouponDiscountUsd())} de descuento`;
    }

    /** El valor del cupón siempre viaja en USD, también para tenants que pagan en VES. */
    private formatCouponAmountUsd(value: number): string {
        return formatCurrency(Number(value ?? 0), 'en-US', '$', 'USD', '1.2-2');
    }

    /** Etiqueta de la línea del cupón en el desglose (paso 1 y paso 2). */
    getCouponSummaryLabel(): string {
        const coupon = this.appliedCoupon;
        if (!coupon) {
            return 'Descuento por cupón';
        }
        return `Descuento por cupón (${coupon.code})`;
    }

    /**
     * Sella el cupón en la factura antes de cobrar. Emite `false` si el sellado falla:
     * en ese caso no se puede pagar, porque los tres caminos de cobro leen el monto de
     * la factura y el comercio pagaría un monto distinto al que vio.
     *
     * No-op cuando no hay cupón validado o ya está sellado. Un segundo subscribe
     * mientras hay apply en vuelo reusa el mismo observable (el doble click no
     * dispara dos apply).
     */
    private ensureCouponSealed$(): Observable<boolean> {
        if (this.isValidatingCoupon || this.hasUnresolvedCouponInput || this.couponPaymentBlocked) {
            return of(false);
        }
        const billPlanId = this.plansService.plansBundle$?.value?.currentBillplan?._id;
        if (!billPlanId) {
            return of(true);
        }
        if (!this.appliedCoupon) {
            if (this.restoreSealedSnapshotIfSelectionMatches()) {
                return of(true);
            }
            if (this.sealedCouponSnapshot || this.getLiveServerCoupon()) {
                this.couponPaymentBlocked = true;
                return of(false);
            }
            return of(true);
        }
        if (this.couponCodeInput && !this.couponCodesMatch(this.couponCodeInput, this.appliedCoupon.code)) {
            return of(false);
        }
        if (this.isCurrentSelectionAlreadySealed()) {
            if (this.isSealedHoldExpired()) {
                this.expireCouponHold({ unsealOnServer: true });
                return of(false);
            }
            return of(true);
        }
        if (this.sealingInFlight$) {
            return this.sealingInFlight$;
        }
        this.isSealingCoupon = true;
        this.sealingInFlight$ = this.plansService.applyCoupon$(billPlanId, this.appliedCoupon.code, this.getCouponSelection()).pipe(
            map(response => {
                this.isSealingCoupon = false;
                this.sealingInFlight$ = null;
                this.couponSealed = true;
                this.couponPaymentBlocked = false;
                if (response?.data?.desglose) {
                    this.couponDesglose = response.data.desglose;
                }
                this.couponReservedUntil = response?.data?.reservedUntil ?? this.couponReservedUntil;
                this.rememberSealedSnapshot();
                this.persistSealedCouponOnBundle();
                this.scheduleCouponHoldExpiry(this.couponReservedUntil);
                this.afterCouponAmountsChanged();
                return true;
            }),
            catchError(error => {
                this.isSealingCoupon = false;
                this.sealingInFlight$ = null;
                this.couponError = error?.error?.message || COUPON_APPLY_FALLBACK_MESSAGE;
                // Carrera: el primer apply de esta sesión ya selló este par.
                if (this.isCurrentSelectionAlreadySealed()) {
                    return of(true);
                }
                // 409 "ya aplicado" no se asume idempotente si no es el sello nuestro
                // con el mismo código y selección. No se borra el preview: pintar
                // precio lleno con la factura sellada mentiría sobre el cobro.
                // `couponPaymentBlocked` impide avanzar hasta un preview nuevo.
                this.couponPaymentBlocked = true;
                this.couponSealed = false;
                return of(false);
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
        );
        return this.sealingInFlight$;
    }

    /** Código escrito sin preview vigente, o preview de otro código. */
    get hasUnresolvedCouponInput(): boolean {
        if (!this.couponCodeInput) {
            return false;
        }
        return !this.appliedCoupon || !this.couponCodesMatch(this.appliedCoupon.code, this.couponCodeInput);
    }

    /**
     * False mientras el cupón no está listo para cobrar: preview/apply en vuelo,
     * código sin validar, o un apply que falló (la factura puede estar sellada).
     */
    canAdvanceWithCoupon(): boolean {
        // El mix no paga la factura del plan: su cupón (vigente o vencido) no bloquea el reporte.
        if (this.isMixPurchaseFlow) {
            return true;
        }
        if (this.isValidatingCoupon || this.isSealingCoupon || this.hasUnresolvedCouponInput || this.couponPaymentBlocked) {
            return false;
        }
        if (this.isSealedHoldExpired()) {
            return false;
        }
        // Sello vivo sin preview: solo se avanza si se puede restaurar el desglose
        // de esa selección. Si no, la UI mostraría precio lleno y el cobro descuento.
        if (!this.appliedCoupon && (this.sealedCouponSnapshot || this.getLiveServerCoupon())) {
            return this.canRestoreSealedSnapshotForCurrentSelection();
        }
        return true;
    }

    private getLiveServerCoupon(): NonNullable<PlansBundle['currentBillplan']>['appliedCoupon'] | null {
        const sealed = this.plansService.plansBundle$?.value?.currentBillplan?.appliedCoupon;
        if (!sealed?.code || sealed.consumedAt) {
            return null;
        }
        return sealed;
    }

    private serverSealMatchesSelection(sealed: NonNullable<ReturnType<FormPayPlanComponent['getLiveServerCoupon']>>): boolean {
        const sealedPlan = sealed.planId != null ? String(sealed.planId) : '';
        const sealedFreq = (sealed.paymentFrequency || '').toString().toLowerCase();
        if (!sealedPlan && !sealedFreq) {
            return !this.hasPlanSelectionChanged() && !this.hasFrequencySelectionChanged();
        }
        const planOk = !sealedPlan || sealedPlan === (this.selectedPlanId || '');
        const freqOk = !sealedFreq || sealedFreq === this.getPaymentFrequencyForApi();
        return planOk && freqOk;
    }

    private canRestoreSealedSnapshotForCurrentSelection(): boolean {
        return !!this.sealedCouponSnapshot && this.sealedCouponSnapshot.selectionKey === this.getCouponSelectionKey();
    }

    private restoreSealedSnapshotIfSelectionMatches(): boolean {
        if (!this.canRestoreSealedSnapshotForCurrentSelection() || !this.sealedCouponSnapshot) {
            return false;
        }
        this.appliedCoupon = this.sealedCouponSnapshot.coupon;
        this.couponDesglose = this.sealedCouponSnapshot.desglose;
        this.couponCodeInput = this.normalizeCouponCode(this.sealedCouponSnapshot.code);
        this.couponSealed = true;
        this.couponPaymentBlocked = false;
        this.couponReservedUntil = this.sealedCouponSnapshot.reservedUntil ?? this.couponReservedUntil;
        this.scheduleCouponHoldExpiry(this.couponReservedUntil);
        this.afterCouponAmountsChanged();
        return true;
    }

    /** Si `GET /plans` ya trae un canje sin consumir, alinear la UI a ese sello. */
    private hydrateSealedCouponFromBill(): void {
        // El mix no paga la factura del plan: hidratar su cupón pisaría los montos del carrito.
        if (this.isMixPurchaseFlow) {
            return;
        }
        const sealed = this.getLiveServerCoupon();
        if (!sealed) {
            return;
        }
        if (this.isReservedUntilExpired(sealed.reservedUntil)) {
            this.couponPanelOpen = true;
            this.couponReservedUntil = sealed.reservedUntil ?? null;
            this.restoreSealedSnapshotIfSelectionMatches();
            this.expireCouponHold({ unsealOnServer: true });
            return;
        }
        if (sealed.planId) {
            this.selectedPlanId = String(sealed.planId);
        }
        if (sealed.paymentFrequency) {
            this.selectedPaymentFrequency = this.mapApiFrequencyToUi(sealed.paymentFrequency);
        }
        this.couponCodeInput = this.normalizeCouponCode(sealed.code);
        this.couponReservedUntil = sealed.reservedUntil ?? null;
        this.couponPanelOpen = true;
        this.scheduleCouponHoldExpiry(this.couponReservedUntil);
        this.isValidatingCoupon = true;
        this.couponCodeTyped$.next(this.couponCodeInput);
    }

    private isCurrentSelectionAlreadySealed(): boolean {
        return (
            this.couponSealed &&
            !!this.appliedCoupon &&
            !!this.sealedCouponSnapshot &&
            this.couponCodesMatch(this.sealedCouponSnapshot.code, this.appliedCoupon.code) &&
            this.sealedCouponSnapshot.selectionKey === this.getCouponSelectionKey()
        );
    }

    private rememberSealedSnapshot(): void {
        if (!this.appliedCoupon || !this.couponDesglose) {
            return;
        }
        this.sealedCouponSnapshot = {
            code: this.appliedCoupon.code,
            coupon: this.appliedCoupon,
            desglose: this.couponDesglose,
            selectionKey: this.getCouponSelectionKey(),
            reservedUntil: this.couponReservedUntil,
        };
    }

    /**
     * El apply no refresca el bundle. Sin este parche, al cerrar la modal se
     * pierde el snapshot y `hydrateSealedCouponFromBill` no tiene
     * `currentBillplan.appliedCoupon` — la UI pintaría precio lleno.
     */
    private persistSealedCouponOnBundle(): void {
        const bundle = this.plansService.plansBundle$?.value;
        if (!bundle?.currentBillplan || !this.appliedCoupon) {
            return;
        }
        const selection = this.getCouponSelection();
        this.plansService.plansBundle$.next({
            ...bundle,
            currentBillplan: {
                ...bundle.currentBillplan,
                appliedCoupon: {
                    ...(bundle.currentBillplan.appliedCoupon || {}),
                    code: this.appliedCoupon.code,
                    discountType: this.appliedCoupon.discountType,
                    value: this.appliedCoupon.value,
                    planId: selection.planType || undefined,
                    paymentFrequency: selection.paymentFrequency || undefined,
                    consumedAt: null,
                    reservedUntil: this.couponReservedUntil,
                },
            },
        });
    }

    /** Deja el bloque de cupón como recién abierto el modal (colapsado y sin descuento). */
    private resetCouponState(): void {
        const liveSealed = this.getLiveServerCoupon();
        this.clearCouponHoldTimer();
        this.couponCodeInput = '';
        this.couponError = '';
        this.isValidatingCoupon = false;
        this.isSealingCoupon = false;
        this.couponPanelOpen = false;
        this.couponPaymentBlocked = false;
        // Si la factura sigue sellada, el snapshot hace falta al reabrir: sin él
        // un unseal fallido pintaría lista con la factura en descuento.
        if (!liveSealed) {
            this.sealedCouponSnapshot = null;
        }
        this.sealingInFlight$ = null;
        this.couponReservedUntil = null;
        this.couponHoldPaymentInFlight = false;
        this.couponHoldExpiredWhilePaying = false;
        this.couponHoldUnsealing = false;
        this.clearAppliedCoupon();
    }

    /** Quita el cupón del resumen; el sello de esta sesión (si lo hay) se conserva. */
    private clearAppliedCoupon(): void {
        this.appliedCoupon = null;
        this.couponDesglose = null;
        this.couponSealed = false;
    }

    private isReservedUntilExpired(reservedUntil?: string | Date | null): boolean {
        if (reservedUntil == null || reservedUntil === '') {
            return false;
        }
        const expiresAt = new Date(reservedUntil).getTime();
        return Number.isFinite(expiresAt) && expiresAt <= Date.now();
    }

    private isSealedHoldExpired(): boolean {
        const until = this.getLiveServerCoupon()?.reservedUntil ?? this.couponReservedUntil ?? this.sealedCouponSnapshot?.reservedUntil;
        return this.isReservedUntilExpired(until);
    }

    private clearCouponHoldTimer(): void {
        if (this.couponHoldTimer) {
            clearTimeout(this.couponHoldTimer);
            this.couponHoldTimer = null;
        }
    }

    private scheduleCouponHoldExpiry(reservedUntil?: string | Date | null): void {
        this.clearCouponHoldTimer();
        if (reservedUntil == null || reservedUntil === '') {
            return;
        }
        const expiresAt = new Date(reservedUntil).getTime();
        if (!Number.isFinite(expiresAt)) {
            return;
        }
        const delayMs = expiresAt - Date.now();
        if (delayMs <= 0) {
            this.onCouponHoldTimerFired();
            return;
        }
        this.couponHoldTimer = setTimeout(() => this.onCouponHoldTimerFired(), delayMs);
    }

    private onCouponHoldTimerFired(): void {
        this.couponHoldTimer = null;
        if (this.loading || this.couponHoldPaymentInFlight) {
            this.couponHoldExpiredWhilePaying = true;
            return;
        }
        this.expireCouponHold({ unsealOnServer: true });
    }

    /**
     * Hold vencido: vuelve al paso 1 y pinta el message en el campo.
     * `unsealOnServer` llama apply con code vacío para soltar el cupo ya.
     * En un 409 de cobro el servidor ya unsealó: solo se limpia la UI.
     */
    private expireCouponHold(options?: { unsealOnServer?: boolean; message?: string }): void {
        const message = options?.message || COUPON_HOLD_EXPIRED_FALLBACK_MESSAGE;
        if (this.couponHoldUnsealing) {
            return;
        }
        this.clearCouponHoldTimer();
        this.couponHoldPaymentInFlight = false;
        this.couponHoldExpiredWhilePaying = false;

        if (!options?.unsealOnServer) {
            this.applyExpiredHoldUi(message);
            return;
        }

        const billPlanId = this.plansService.plansBundle$?.value?.currentBillplan?._id;
        if (!billPlanId) {
            this.applyExpiredHoldUi(message);
            return;
        }

        this.couponHoldUnsealing = true;
        this.couponPaymentBlocked = true;
        this.plansService
            .applyCoupon$(billPlanId, '')
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.couponHoldUnsealing = false;
                    this.couponPaymentBlocked = false;
                    this.applyExpiredHoldUi(message);
                    this.plansService.obtainPlansBundle();
                },
                error: (error: any) => {
                    this.couponHoldUnsealing = false;
                    // El sello sigue en la factura: no fingir precio de lista ni
                    // recargar el bundle (reinstalaría appliedCoupon y trabaría el campo).
                    this.couponError = error?.error?.message || message;
                    this.couponPaymentBlocked = true;
                    this.couponPanelOpen = true;
                    this.goBackToPlanStep();
                },
            });
    }

    private applyExpiredHoldUi(message: string): void {
        this.patchBundleClearCoupon();
        this.sealedCouponSnapshot = null;
        this.couponReservedUntil = null;
        this.clearAppliedCoupon();
        this.couponCodeInput = '';
        this.couponPanelOpen = true;
        this.couponError = message;
        this.goBackToPlanStep();
    }

    private patchBundleClearCoupon(): void {
        const bundle = this.plansService.plansBundle$?.value;
        if (!bundle?.currentBillplan) {
            return;
        }
        this.plansService.plansBundle$.next({
            ...bundle,
            currentBillplan: {
                ...bundle.currentBillplan,
                appliedCoupon: undefined,
            },
        });
    }

    isCouponHoldExpiredError(err: any): boolean {
        return err?.error?.title === COUPON_HOLD_EXPIRED_TITLE;
    }

    onCouponHoldExpiredFromCard(message?: string): void {
        this.couponHoldPaymentInFlight = false;
        this.expireCouponHold({ unsealOnServer: false, message: message || COUPON_HOLD_EXPIRED_FALLBACK_MESSAGE });
    }

    onCardChargeFailed(): void {
        if (this.isMixPurchaseFlow) {
            // `chargeFailed` es `EventEmitter<void>`: el rechazo no trae status HTTP.
            this.analyticsService.analyticsCapture(MIX_PURCHASE_EVENTS.cardFailed, { ...this.mixAnalyticsProps(), reason: 'rejected', http_status: null });
        }
        this.settleCardChargeAttempt();
    }

    /**
     * Cierre del intento de cobro que no prosperó: suelta el «cobro en vuelo» del hold del
     * cupón y, si el hold venció mientras tanto, lo libera en el servidor. Lo comparten el
     * rechazo de tarjeta y el error del cupo fiscal (tarjeta o Débito Inmediato).
     */
    private settleCardChargeAttempt(): void {
        this.couponHoldPaymentInFlight = false;
        if (this.couponHoldExpiredWhilePaying || this.isSealedHoldExpired()) {
            this.expireCouponHold({ unsealOnServer: true });
        }
    }

    /**
     * Errores de `payMixWithCard` que no son un rechazo de tarjeta (no emiten `chargeFailed`):
     * la renovación vencida corta la compra (resultado terminal); el resto es reintentable.
     */
    onMixChargeIssue(issue: AutoPayCardMixChargeIssue): void {
        if (!this.isMixPurchaseFlow) {
            return;
        }
        if (issue?.reason === 'renewal_due') {
            this.trackMixBlocked('card_charge');
            return;
        }
        this.analyticsService.analyticsCapture(MIX_PURCHASE_EVENTS.cardFailed, { ...this.mixAnalyticsProps(), reason: 'request_error', http_status: issue?.httpStatus ?? null });
    }

    /** Un cambio de montos invalida la referencia ya verificada y el payload del pago. */
    private afterCouponAmountsChanged(): void {
        this.resetValidationState();
        this.syncAmountsFromSelection();
    }

    /**
     * Al cambiar plan o frecuencia el cupón puede seguir siendo válido o dejar de
     * serlo (`applicablePlans` / `applicableFrequencies` se validan contra la
     * selección), así que se revalida con el mismo código en vez de descartarlo.
     *
     * El descuento se retira mientras tanto: el desglose que había es de la selección
     * anterior. Si el cupón ya no aplica, el rechazo de api-admin lo explica en el
     * campo. `clearAppliedCoupon` deja `couponSealed` en false, así que el sellado se
     * rehace con la selección nueva antes de cobrar.
     */
    private revalidateCouponOnSelectionChange(): void {
        if (!this.appliedCoupon && !this.couponCodeInput) {
            return;
        }
        this.clearAppliedCoupon();
        this.couponError = '';
        this.isValidatingCoupon = !!this.couponCodeInput;
        this.couponCodeTyped$.next(this.couponCodeInput);
    }

    /** Tenant en modalidad plus: se suma IVA al total del pago del plan. */
    isCommercePlusIva(): boolean {
        return this.commerceService.commerce?.plus === true;
    }

    /**
     * Cambio mid-cycle: selector de plan/frecuencia con tenant/factura ya en pago.
     * Aplica en `change-plan` y también en `buy-sms` si el usuario pasa al tab de plan
     * (ambos modos comparten el mismo selector).
     *
     * AND, no OR: es el mismo criterio que `resolveMidCycleChangeFlags` en api-admin
     * (tenant en `pago` Y factura origen en `pago`). Con OR, una factura `pendiente`
     * nacida de un split de fechas —con `nextPayment.paymentStatus` aún en `pago`—
     * mostraba el fee de $5 × meses mientras el servidor rechazaba cada intento de
     * pago con 409 "Cambio no permitido". Ese caso es una renovación normal.
     *
     * También false si hay cobro mid-cycle pendiente (`hasUnresolvedMidCycleChange`).
     */
    isMidCycleChange(bundle?: PlansBundle): boolean {
        if (this.modalMode !== 'change-plan' && this.modalMode !== 'buy-sms') {
            return false;
        }
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!bundle || bundle.hasUnresolvedMidCycleChange) {
            return false;
        }
        const billStatus = (bundle.currentBillplan?.status || '').toLowerCase();
        const paymentStatus = (bundle.nextPayment?.paymentStatus || '').toLowerCase();
        return billStatus === 'pago' && paymentStatus === 'pago';
    }

    /**
     * Tenant ya en `pago` pero la factura vigente NO lo está (split de fechas,
     * `paymentStatus` desincronizado). api-admin rechaza con 409 cualquier cambio de
     * plan o frecuencia en ese estado (`assertMidCycleSourceBillIsPaid`): lo único
     * válido es pagar la factura pendiente con el plan actual. No se acota por
     * `modalMode` — el paso 1 permite cambiar de plan también en modo `pay`.
     */
    isPlanChangeBlockedByPendingBill(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!bundle) {
            return false;
        }
        const billStatus = (bundle.currentBillplan?.status || '').toLowerCase();
        const paymentStatus = (bundle.nextPayment?.paymentStatus || '').toLowerCase();
        return paymentStatus === 'pago' && billStatus !== 'pago';
    }

    hasPlanSelectionChanged(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        const currentPlanId = this.getCurrentPlanId(bundle);
        return !!currentPlanId && !!this.selectedPlanId && this.selectedPlanId !== currentPlanId;
    }

    hasFrequencySelectionChanged(bundle?: PlansBundle): boolean {
        const currentFrequency = this.mapBundlePaymentFrequencyToUi();
        return this.selectedPaymentFrequency !== currentFrequency;
    }

    /**
     * En mid-cycle hace falta cambiar plan y/o frecuencia para proceder; con una
     * factura pendiente de por medio pasa lo contrario: hay que dejar la selección
     * como está (el servidor rechaza el cambio).
     */
    canProceedToPayment(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (this.isPlanChangeBlockedByPendingBill(bundle)) {
            return !this.hasPlanSelectionChanged(bundle) && !this.hasFrequencySelectionChanged(bundle);
        }
        if (!this.isMidCycleChange(bundle)) {
            return true;
        }
        return this.hasPlanSelectionChanged(bundle) || this.hasFrequencySelectionChanged(bundle);
    }

    /**
     * El cobro con tarjeta aplica a pago de plan, cambio de plan/frecuencia y
     * compra de SMS (create-and-charge).
     *
     * Es el gate único de la tarjeta: preselección (`enterPaymentStep`, carga de
     * `hasSavedCard`), botón, «Cambiar método de pago» y `fina-auto-pay-card`.
     */
    canUseCardForSelection(bundle?: PlansBundle): boolean {
        const modeAllowsCard =
            this.modalMode === 'pay' || this.modalMode === 'change-plan' || this.modalMode === 'buy-sms' || this.modalMode === 'mix' || this.isSmsPurchaseFlow || this.isMixPurchaseFlow;
        return modeAllowsCard && this.cardBlockedByCouponMessage(bundle) === null;
    }

    /**
     * Por qué la tarjeta no sirve con el cupón vivo, o `null`. Cupos primero (api-admin los
     * evalúa antes que el cupo fiscal): con los dos activos se ve un solo aviso.
     */
    cardBlockedByCouponMessage(bundle?: PlansBundle): string | null {
        if (this.isCardBlockedByCouponSeatsChange(bundle)) {
            return this.couponSeatsCardBlockedMessage;
        }
        if (this.isCardBlockedByCouponFiscalQuotaChange(bundle)) {
            return this.couponFiscalCardBlockedMessage;
        }
        return null;
    }

    /**
     * Cupón + cambio de la cantidad de facturas + cambio de plan o frecuencia: api-admin
     * rechaza ese cobro con tarjeta (409 `COUPON_FISCAL_QUOTA_CHANGE_UNSUPPORTED`). Pago móvil
     * y Débito Inmediato sí lo aceptan, así que solo se retira la tarjeta (ADM-740).
     */
    isCardBlockedByCouponFiscalQuotaChange(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!this.appliedCoupon && !this.getLiveServerCoupon()) {
            return false;
        }
        if (!this.hasExtraControlNumbersIntent(bundle)) {
            return false;
        }
        if (!this.hasPlanSelectionChanged(bundle) && !this.hasFrequencySelectionChanged(bundle)) {
            return false;
        }
        // api-admin compara la cantidad pedida con la SELLADA, no con la facturada.
        return this.getRequestedExtraControlNumbers(bundle) !== (this.getSealedFiscalQuota(bundle)?.quantity ?? 0);
    }

    /** `message` del 409 fiscal de api-admin; sin Débito Inmediato en pantalla, solo pago móvil. */
    get couponFiscalCardBlockedMessage(): string {
        return this.isDebitoInmediatoEligible() ? COUPON_FISCAL_QUOTA_CARD_BLOCKED_MESSAGE : COUPON_FISCAL_QUOTA_CARD_BLOCKED_MESSAGE_WITHOUT_DEBIT;
    }

    /**
     * Cupón + cambio de cupos + cambio de plan o frecuencia: api-admin rechaza ese
     * cobro con tarjeta (409 «Cupón y cupos no combinables») porque el total
     * sellado no cubre los cupos nuevos. Pago móvil y Débito Inmediato sí lo
     * aceptan, así que solo se retira la tarjeta. `canEditExtraUsers` ya exige
     * modo `pay` y descarta los flujos SMS y mix.
     */
    isCardBlockedByCouponSeatsChange(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        if (!this.appliedCoupon && !this.getLiveServerCoupon()) {
            return false;
        }
        if (!this.canEditExtraUsers(bundle) || this.getExtraUsersDeltaBaseUsd(bundle) === 0) {
            return false;
        }
        return this.hasPlanSelectionChanged(bundle) || this.hasFrequencySelectionChanged(bundle);
    }

    /**
     * Aviso del paso 2 cuando la tarjeta se retira por cupón + cupos + cambio. Es
     * el `message` del 409 de api-admin; sin Débito Inmediato en pantalla, la
     * variante solo ofrece pago móvil para no prometer un método que no está.
     */
    get couponSeatsCardBlockedMessage(): string {
        return this.isDebitoInmediatoEligible() ? COUPON_SEATS_CARD_BLOCKED_MESSAGE : COUPON_SEATS_CARD_BLOCKED_MESSAGE_WITHOUT_DEBIT;
    }

    /** planType a enviar al cobro con tarjeta solo si el plan cambió. */
    getCardPayPlanType(): string | null {
        return this.hasPlanSelectionChanged() ? this.selectedPlanId || null : null;
    }

    /** paymentFrequency a enviar al cobro con tarjeta solo si la frecuencia cambió. */
    getCardPayPaymentFrequency(): string | null {
        return this.hasFrequencySelectionChanged() ? this.getPaymentFrequencyForApi() : null;
    }

    /** Solo cambio de plan mid-cycle → fee $5 × meses restantes (+ IVA si plus). */
    isMidCyclePlanOnlyFee(bundle?: PlansBundle): boolean {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        return this.isMidCycleChange(bundle) && this.hasPlanSelectionChanged(bundle) && !this.hasFrequencySelectionChanged(bundle);
    }

    /** Meses restantes del ciclo pagado (mes empezado = completo). */
    getMidCycleRemainingMonths(bundle?: PlansBundle): number {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        const paymentDateRaw = bundle?.nextPayment?.date || bundle?.currentBillplan?.paymentDatePlan || null;
        if (!paymentDateRaw) {
            return 1;
        }
        const end = moment.utc(paymentDateRaw).startOf('month');
        const start = moment.utc().startOf('month');
        if (!end.isValid() || !start.isValid()) {
            return 1;
        }
        return Math.max(1, end.diff(start, 'months') + 1);
    }

    getMidCyclePlanOnlyFeeBaseUsd(bundle?: PlansBundle): number {
        return Number((this.midCyclePlanChangeFeePerMonthUsd * this.getMidCycleRemainingMonths(bundle)).toFixed(2));
    }

    /**
     * Base USD imponible: tras descuento por frecuencia y saldo a favor, sin IVA.
     * Con cupón validado es `desglose.baseUsd` (ya trae cupón y saldo descontados).
     */
    getNetAmountBeforeIvaUsd(bundle: PlansBundle): number {
        const desglose = this.activeCouponDesglose;
        if (desglose) {
            const fiscalDelta = this.getExtraControlNumbersDelta(bundle).deltaBase;
            return Number((Number(desglose.baseUsd ?? 0) + this.getExtraUsersDeltaBaseUsd(bundle) + fiscalDelta).toFixed(2));
        }
        if (!bundle?.currentBillplan) {
            return 0;
        }
        if (this.isMidCyclePlanOnlyFee(bundle)) {
            return this.getMidCyclePlanOnlyFeeBaseUsd(bundle);
        }
        const bill = bundle.currentBillplan;
        const monthly = this.getMonthlyBaseUsd(bundle);
        const months = this.getMonthsForFrequency();
        const periodGrossUsd = Number((monthly * months).toFixed(2));
        const freqDiscountUsd = this.getFrequencyDiscountUsd(bundle);
        const afterFrequencyUsd = Number((periodGrossUsd - freqDiscountUsd).toFixed(2));
        const creditUsd = this.getCreditDiscountUsd(bundle);
        const extraUsersUsd = this.getExtraUsersBaseUsd(bundle);
        const extraControlNumbersUsd = this.getExtraControlNumbersBaseUsd(bundle);
        return Math.max(0, Number((afterFrequencyUsd - creditUsd + extraUsersUsd + extraControlNumbersUsd).toFixed(2)));
    }

    /** IVA (16%) en USD sobre la base neta; solo si `commerce.plus`. */
    getSummaryIvaUsd(bundle: PlansBundle): number {
        const desglose = this.activeCouponDesglose;
        if (desglose) {
            const extrasIva = this.isCommercePlusIva() ? Number((this.getExtraUsersDeltaBaseUsd(bundle) * this.planPaymentIvaRate).toFixed(2)) : 0;
            const fiscalIva = this.getExtraControlNumbersDelta(bundle).deltaPlus;
            return Number((Number(desglose.ivaUsd ?? 0) + extrasIva + fiscalIva).toFixed(2));
        }
        if (!this.isCommercePlusIva()) {
            return 0;
        }
        const net = this.getNetAmountBeforeIvaUsd(bundle);
        return Number((net * this.planPaymentIvaRate).toFixed(2));
    }

    /** Recalcula amount / amountVES / amountPlan / paymentFrequency según plan y frecuencia. */
    syncAmountsFromSelection(): void {
        const bundle = this.plansService.plansBundle$?.value;
        if (!bundle?.currentBillplan) {
            return;
        }
        const bill = bundle.currentBillplan;
        const exchangeRateBCV = Number((this.payFormData.exchangeRateBCV || this.commerceService.exchangeRateBCV$?.getValue() || 0).toFixed(2));

        const monthly = this.isMidCyclePlanOnlyFee(bundle) ? this.getMidCyclePlanOnlyFeeBaseUsd(bundle) : this.getMonthlyBaseUsd(bundle);
        const ivaUsd = this.getSummaryIvaUsd(bundle);
        // Con cupón el total es el del servidor (incluye la retención, que el cálculo
        // cliente no contempla): la conversión a VES es lo único que sigue aquí.
        const amountUsd = this.getSummaryTotalUsd(bundle);
        const amountVES = Number((amountUsd * exchangeRateBCV).toFixed(2));

        this.payFormData = {
            ...this.payFormData,
            billPlanId: bill._id,
            amountPlan: monthly,
            amount: amountUsd,
            amountVES,
            exchangeRateBCV,
            paymentFrequency: this.getPaymentFrequencyForApi(),
            plus: ivaUsd,
        };
    }

    getPaymentMethods() {
        this.loadingPaymentMethods = true;
        this.plansService
            .getPaymentMethods$()
            .pipe(
                map(res => {
                    var shouldShowBncEfectivo = this.analyticsService.getExperimentVariant('payment-methods-bnc-efectivo');
                    if (!shouldShowBncEfectivo) {
                        // Filtrar metodo de pago BNC Efectivo
                        res.data = res.data.filter((pm: any) => pm._id !== this.BNC_EFECTIVO_STAGING_ID && pm._id !== this.BNC_EFECTIVO_PROD_ID);
                    }

                    return res;
                }),
            )
            .subscribe({
                next: (res: any) => {
                    // El backend ya devuelve solo los métodos visibles para este tenant
                    // (dirigido por los flags showToClient + plus de la BD). No filtramos
                    // por nombre aquí para no reintroducir condiciones hardcodeadas.
                    this.paymentMethods = res.data;
                    this.migrationNotice = res.migrationNotice ?? { bankAccountChanged: false, requiresFiscalInfo: false };
                    // Sin preselección: el usuario elige el método explícitamente.
                    this.paymentMethodSelected = null;
                    this.payFormData.paymentMethod = '';
                    this.loadingPaymentMethods = false;
                },
                error: (err: any) => {
                    this.loadingPaymentMethods = false;
                    this.toastService.error(err.error.title, err.error.message);
                },
            });
    }

    /** El usuario suele copiar el RIF con guiones o puntos ("J-40.123.456-7"). */
    private get normalizedTaxIdNumber(): string {
        return (this.fiscalForm.taxIdNumber || '').replace(/[-.\s]/g, '').trim();
    }

    get isFiscalTaxIdValid(): boolean {
        return this.taxIdTypes.includes(this.fiscalForm.taxIdType) && isValidTaxId(this.fiscalForm.taxIdType, this.normalizedTaxIdNumber);
    }

    /** Solo se muestra cuando ya escribió algo: no se le grita a un campo vacío. */
    get fiscalTaxIdError(): string | null {
        if (!this.normalizedTaxIdNumber || this.isFiscalTaxIdValid) {
            return null;
        }
        return this.fiscalForm.taxIdType === 'V' || this.fiscalForm.taxIdType === 'E'
            ? 'Una cédula lleva entre 7 y 9 dígitos, sin guiones.'
            : 'Un RIF de empresa lleva 9 dígitos, incluido el dígito verificador.';
    }

    get isFiscalFormValid(): boolean {
        return this.isFiscalTaxIdValid && (this.fiscalForm.address || '').trim().length > 0;
    }

    /**
     * Guarda el RIF del comercio durante la migración. La verificación con el
     * SENIAT es asíncrona (webhook), así que acá solo confirmamos la recepción.
     */
    saveFiscalInfo(): void {
        if (!this.isFiscalFormValid || this.savingFiscalInfo) {
            return;
        }

        this.savingFiscalInfo = true;
        this.plansService
            .saveFiscalInfo$({
                taxIdType: this.fiscalForm.taxIdType,
                taxIdNumber: this.fiscalForm.taxIdNumber,
                address: this.fiscalForm.address,
            })
            .subscribe({
                next: (res: any) => {
                    this.savingFiscalInfo = false;
                    this.fiscalInfoSaved = true;
                    this.migrationNotice = { ...this.migrationNotice, requiresFiscalInfo: false };
                    this.toastService.success(res?.title ?? 'Datos guardados', res?.message ?? '');
                },
                error: (err: any) => {
                    this.savingFiscalInfo = false;
                    this.toastService.error(err?.error?.title ?? 'Error', err?.error?.message ?? 'No se pudieron guardar los datos fiscales.');
                },
            });
    }

    payPlan() {
        this.invalidateStaleValidation();
        if (!this.pagoMovilSelected || this.cardMethodSelected || this.isValidationRequired() || this.isBlockedByMissingFiscalInfo) {
            return;
        }

        if (this.isSmsPurchaseFlow) {
            this.reportSmsPurchase();
            return;
        }
        if (this.isMixPurchaseFlow) {
            this.reportMixPurchase();
            return;
        }

        // Red de seguridad del sellado: normalmente ya ocurrió al entrar al paso 2.
        // Si por lo que sea no está sellado, se sella aquí y un fallo aborta el pago.
        // `canAdvanceWithCoupon` cubre preview en vuelo, apply fallido y código
        // sin validar: sin esto, un apply 409 que borrara el preview (antes)
        // dejaba el siguiente click cobrar a precio lleno sobre una factura sellada.
        if (!this.canAdvanceWithCoupon()) {
            return;
        }
        this.ensureCouponSealed$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(sealed => {
                if (sealed) {
                    this.sendPlanPayment();
                }
            });
    }

    /** POST /plans/pay con el payload ya sincronizado (y el cupón sellado si había). */
    private sendPlanPayment(): void {
        this.loading = true;
        this.couponHoldPaymentInFlight = true;
        this.lastPaymentDate = this.plansService.plansBundle$?.value.currentBillplan?.paymentDatePlan;
        const isoDate = this.toIsoDate(this.fromDate);
        // Se captura al enviar: el 400 de cantidad solo es del cupo fiscal si el campo viajó.
        const fiscalIntentSent = this.hasExtraControlNumbersIntent();

        const paymentData = {
            billPlanId: this.payFormData.billPlanId,
            paymentDate: isoDate,
            paidDate: isoDate,
            paymentMethod: this.payFormData.paymentMethod,
            ref: this.payFormData.ref,
            amount: this.payFormData.amount,
            amountPlan: this.payFormData.amountPlan,
            amountVES: this.payFormData.amountVES,
            exchangeRateBCV: this.payFormData.exchangeRateBCV,
            issuing: this.payFormData.issuing,
            comments: this.payFormData.comments,
            paymentFrequency: this.payFormData.paymentFrequency,
            plus: this.payFormData.plus,
            planType: this.selectedPlanId,
            pendingPurchasedUsers: this.hasExtraUsersIntent() ? this.pendingPurchasedUsers : undefined,
            pendingBlockUserIds: this.hasExtraUsersIntent() ? this.pendingBlockUserIds : undefined,
            // Sin intención es `undefined` y no viaja: el body queda idéntico al de hoy.
            extraControlNumbers: fiscalIntentSent ? this.getRequestedExtraControlNumbers() : undefined,
        };

        this.plansService
            .payPlan$(paymentData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.analyticsService.analyticsCapture('Pago registrado');
                    const currentPlansBundle = this.plansService.plansBundle$.value;
                    const newPlansBundle = {
                        ...currentPlansBundle,
                        statusTenant: 'ACTIVE',
                        nextPayment: res.data.nextPayment,
                        paidDates: res.data.paidDates,
                        isInProcess: res.data.isInProcess,
                        currentBillplan: res.data.currentBillplan,
                        billsplan: res.data.billsplan,
                    };
                    this.planPaymentSuccessMessage = this.applyRegistrationResult(res?.data, res?.message, 'Pago reportado correctamente.');
                    this.showRegistrationToast(res?.title, this.planPaymentSuccessMessage);
                    this.plansService.refreshPaymentHistory();
                    if (!this.validateDatePlan()) {
                        this.postPaymentSurveyStep = true;
                        this.payModalStep = 2;
                    } else {
                        this.postPaymentSurveyStep = false;
                        this.goToSuccessStep();
                    }
                    this.plansService.plansBundle$.next(newPlansBundle);
                    this.couponHoldPaymentInFlight = false;
                    this.clearCouponHoldTimer();
                    this.loading = false;
                },
                error: (err: any) => {
                    this.loading = false;
                    this.couponHoldPaymentInFlight = false;
                    if (this.isCouponHoldExpiredError(err)) {
                        this.expireCouponHold({
                            unsealOnServer: false,
                            message: err?.error?.message || COUPON_HOLD_EXPIRED_FALLBACK_MESSAGE,
                        });
                        return;
                    }
                    if (err?.status === 409) {
                        this.resetValidationState();
                        this.plansService.obtainPlansBundle();
                    }
                    this.showRegistrationError(err, 'No se pudo registrar el pago.');
                    const fiscalIssue = fiscalQuotaIssue(err, { intentSent: fiscalIntentSent });
                    if (fiscalIssue) {
                        this.onFiscalQuotaConflict(fiscalIssue, 'pago_movil');
                    }
                    if (this.couponHoldExpiredWhilePaying || this.isSealedHoldExpired()) {
                        this.expireCouponHold({ unsealOnServer: true });
                    }
                },
            });
    }

    /** Reporta compra mix vía POST /plans/pay. */
    private reportMixPurchase(): void {
        if (!this.mixCart || !this.hasSelectionMix()) {
            this.toastService.error('Carrito vacío', 'Selecciona complementos para continuar.');
            return;
        }
        // La fecha de pago pudo vencer con el modal abierto: api-admin rechazaría los cupos (409), y
        // la recotización dejaría `amount` en 0 con cupos, que el proxy de api rechaza antes (400).
        if (this.isMixSeatsRenewalDue()) {
            this.trackMixBlocked('report_precheck');
            this.toastService.alert('Renovación pendiente', EXTRA_USERS_MIX_RENEWAL_DUE_MESSAGE, 8000);
            return;
        }
        this.loading = true;
        // Se reporta con la tasa que se MOSTRÓ (la de `payFormData`, la del VES transferido): nada de recargarla aquí.
        this.syncMixAmountsFromCart();
        const isoDate = this.toIsoDate(this.fromDate);
        const controlNumbers = Number(this.mixCart.controlNumbersQuantity || 0);
        const includesTypes = mixCartItems(this.mixCart);
        this.plansService
            .payPlan$({
                paymentType: 'mix',
                includesTypes,
                sms: this.mixCart.smsQuantity || undefined,
                controlNumbers: controlNumbers > 0 ? controlNumbers : undefined,
                extraUsersToAdd: this.mixCart.extraUsersToAdd || undefined,
                includeSetup: this.mixCart.includeSetup || undefined,
                paymentDate: isoDate,
                paidDate: isoDate,
                paymentMethod: this.payFormData.paymentMethod,
                ref: this.payFormData.ref,
                amount: this.payFormData.amount,
                amountVES: this.payFormData.amountVES,
                exchangeRateBCV: this.payFormData.exchangeRateBCV,
                issuing: this.payFormData.issuing,
                comments: this.payFormData.comments,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.analyticsService.analyticsCapture('Compra mix reportada');
                    this.planPaymentSuccessMessage = this.applyRegistrationResult(res?.data, res?.message, 'Pago de complementos reportado.');
                    this.mixCheckoutSettled = true;
                    this.analyticsService.analyticsCapture(MIX_PURCHASE_EVENTS.reported, {
                        ...this.mixAnalyticsProps(),
                        payment_channel: 'pago_movil',
                        under_review: this.paymentUnderReview,
                        verification_outcome: res?.data?.paymentVerification?.outcome ?? null,
                        payment_currency: this.getStep2CurrencyCode(),
                    });
                    this.showRegistrationToast(res?.title, this.planPaymentSuccessMessage);
                    this.plansService.refreshPaymentHistory();
                    this.plansService.obtainPlansBundle();
                    this.postPaymentSurveyStep = false;
                    this.loading = false;
                    this.mixPurchaseRegistered.emit();
                    this.goToSuccessStep();
                },
                error: (err: any) => {
                    this.loading = false;
                    this.trackMixReportError(err);
                    this.showRegistrationError(err, 'No se pudo registrar la compra.');
                },
            });
    }

    /**
     * Props comunes de los eventos mix: siempre del `mixCart` completo y del bundle vigente.
     * `extra_users_months` se cuenta con el mismo instante que la cotización (`mixQuoteNow`).
     */
    private mixAnalyticsProps(): MixCartAnalyticsProps {
        return buildMixCartProps(this.mixCart, this.plansService.plansBundle$?.value ?? null, this.mixQuoteNow());
    }

    /** Compra mix cortada por la renovación vencida con el modal abierto: resultado terminal. */
    private trackMixBlocked(stage: Exclude<MixPurchaseBlockedStage, 'intent'>): void {
        this.mixCheckoutSettled = true;
        this.analyticsService.analyticsCapture(MIX_PURCHASE_EVENTS.blocked, { ...this.mixAnalyticsProps(), reason: 'renewal_due', stage });
    }

    /** Error del reporte mix: el 409 de renovación es un bloqueo; mid-cycle no aplica al mix; el resto, un fallo reintentable. */
    private trackMixReportError(err: any): void {
        if (err?.status === 409 && err?.error?.errorCode === EXTRA_USERS_MIX_RENEWAL_DUE) {
            this.trackMixBlocked('report');
            return;
        }
        if (err?.status === 409 && err?.error?.title === MID_CYCLE_NOT_ALLOWED_TITLE) {
            return;
        }
        this.analyticsService.analyticsCapture(MIX_PURCHASE_EVENTS.reportFailed, {
            ...this.mixAnalyticsProps(),
            http_status: err?.status ?? null,
            error_code: err?.error?.errorCode ?? err?.error?.code ?? null,
        });
    }

    private hasSelectionMix(): boolean {
        return Boolean(this.mixCart && (this.mixCart.smsQuantity > 0 || Number(this.mixCart.controlNumbersQuantity || 0) > 0 || this.mixCart.extraUsersToAdd > 0 || this.mixCart.includeSetup));
    }

    /**
     * El carrito mix lleva cupos y la próxima fecha de pago ya venció (meses 0): no se cotizan ni
     * se envían. Por banco se evalúa con el día del pago reportado (`mixQuoteNow`), como api-admin.
     */
    isMixSeatsRenewalDue(): boolean {
        if (!this.mixCart?.extraUsersToAdd) {
            return false;
        }
        const bundle = this.plansService.plansBundle$?.value;
        return mixExtraUsersMonths(bundle?.nextPayment?.dateToPay ?? null, bundle?.paymentFrequency ?? null, this.mixQuoteNow()) === 0;
    }

    /**
     * Cotiza el mix. La tasa va SIN `toFixed(2)`: api-admin compara el VES contra la tasa oficial
     * sin redondear (tolerancia 0,05 Bs), y con la tasa redondeada el caso normal caía en revisión.
     * En el mix, `payFormData.exchangeRateBCV` guarda la tasa cruda (lo aseguran `openMix`, la
     * suscripción a `exchangeRateBCV$` y `applyMixDayRate`). Con la tasa de otro día
     * (`mixDayRate`, el del pago reportado) manda esa, y los cupos se cotizan con ese día.
     */
    syncMixAmountsFromCart(): void {
        if (!this.mixCart) {
            return;
        }
        const exchangeRateBCV = this.mixDayRate?.rate ?? (Number(this.payFormData.exchangeRateBCV) || Number(this.commerceService.exchangeRateBCV$?.getValue()) || 0);
        const bundle = this.plansService.plansBundle$?.value;
        const quote = quoteMixPurchase({
            smsPriceUsd: Number(this.mixCart.smsPriceUsd || 0),
            smsQuantity: this.mixCart.smsQuantity,
            controlNumbersPriceUsd: Number(this.mixCart.controlNumbersPriceUsd || 0),
            controlNumbersQuantity: this.mixCart.controlNumbersQuantity,
            extraUsersToAdd: this.mixCart.extraUsersToAdd,
            // El precio que cotizó «Mejora tu Fina» manda sobre el del bundle.
            extraUserPriceUsd: getExtraUserPriceUsd({ extraUserPriceUsd: this.mixCart.extraUserPriceUsd ?? bundle?.extraUserPriceUsd }),
            includeSetup: this.mixCart.includeSetup,
            plus: this.commerceService.commerce?.plus === true,
            setup: bundle?.setupService,
            exchangeRateBCV,
            // Los cupos cuestan los meses que faltan hasta la próxima fecha de pago (api-admin
            // valida el `amount` por banco con ±0,05): misma referencia que «Mejora tu Fina».
            referenceDate: bundle?.nextPayment?.dateToPay ?? null,
            paymentFrequency: bundle?.paymentFrequency ?? null,
            // Por banco, los meses se cuentan desde el día del pago reportado (acotado a ayer).
            now: this.mixQuoteNow(),
        });
        this.mixQuote = quote;
        this.mixCart = {
            ...this.mixCart,
            amountUsd: quote.amountUSD,
            subtotalUsd: quote.baseUSD,
            ivaUsd: quote.plusUSD,
            retentionUsd: quote.retentionUSD,
        };
        this.payFormData = {
            ...this.payFormData,
            amount: quote.amountUSD,
            amountVES: quote.amountVES,
            exchangeRateBCV,
            billPlanId: '',
        };
    }

    /** Reporta compra de SMS vía POST /sms/buy (proxy a admin buy-sms). */
    private reportSmsPurchase(): void {
        if (!this.selectedSmsPackage?.quantity) {
            this.toastService.error('Paquete requerido', 'Selecciona un paquete de SMS para continuar.');
            return;
        }

        this.loading = true;
        this.syncSmsAmountsFromSelection();

        const isVes = this.isStep2PaymentCurrencyVes();
        const rate = Number(this.payFormData.exchangeRateBCV || 0);
        const amountUsd = Number(this.payFormData.amount || 0);

        const payload: BuySmsRequest = {
            sms: this.selectedSmsPackage.quantity,
            amount: isVes ? Number((amountUsd * rate).toFixed(2)) : amountUsd,
            paymentMethod: this.payFormData.paymentMethod,
            ref: this.payFormData.ref,
            currencyType: (isVes ? 'VES' : 'USD') as 'VES' | 'USD',
        };
        if (isVes) {
            payload.exchangeRateBCV = rate;
        }

        this.smsService
            .buySms$(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: res => {
                    this.analyticsService.analyticsCapture('Compra SMS reportada', {
                        sms: payload.sms,
                        amount: payload.amount,
                    });
                    this.smsPurchaseSuccessMessage = this.applyRegistrationResult(res?.data, res?.message, this.getSmsPurchaseFallbackMessage(res?.data?.effectiveBillStatus));
                    this.showRegistrationToast(res?.title, this.smsPurchaseSuccessMessage);
                    this.plansService.refreshPaymentHistory();
                    this.postPaymentSurveyStep = false;
                    this.loading = false;
                    this.goToSuccessStep();
                },
                error: (err: any) => {
                    this.loading = false;
                    this.showRegistrationError(err, 'No se pudo registrar la compra de SMS.');
                },
            });
    }

    /**
     * Mensaje público del reporte y estado del paso 3. En revisión manual no se muestra el
     * `summary` del banco («Referencia no encontrada»…): se explica qué pasó y qué sigue.
     * `rateReview` (mix, ADM-732): el VES no cuadró con la tasa oficial; el pago quedó en
     * revisión y los complementos NO se acreditaron. Su `message` es público y va tal cual.
     */
    private applyRegistrationResult(data: any, responseMessage: string | undefined, fallback: string): string {
        if (data?.rateReview != null) {
            this.paymentUnderReview = true;
            return responseMessage || 'Pago de complementos registrado. Queda en revisión.';
        }
        const verification = data?.paymentVerification;
        const applicationStatus = verification?.applicationStatus ?? data?.applicationStatus;
        this.paymentUnderReview = applicationStatus === 'manual_review' || data?.effectiveBillStatus === 'procesando' || data?.paymentValidated === false;
        if (this.paymentUnderReview) {
            return this.buildManualReviewMessage(verification?.outcome, verification?.reference || this.payFormData.ref);
        }
        return responseMessage || verification?.summary || fallback;
    }

    private buildManualReviewMessage(outcome: PaymentVerificationOutcome | string | undefined, reference?: string | null): string {
        const next = 'Lo aplicaremos en cuanto FINA lo confirme.';
        switch (outcome) {
            case 'not_found':
                return `Tu pago quedó en revisión manual: el banco todavía no muestra la referencia ${reference || 'reportada'}. ${next}`;
            case 'date_mismatch':
                return `Tu pago quedó en revisión manual: la fecha que indicaste no coincide con la del banco. ${next}`;
            case 'amount_below_tolerance':
            case 'amount_mismatch':
            case 'overpayment_within_tolerance':
            case 'overpayment_above_tolerance':
                return `Tu pago quedó en revisión manual: el monto pagado no coincide con el total. ${next}`;
            case 'duplicate_reference':
                return `Tu pago quedó en revisión manual: esa referencia ya estaba registrada en otro pago. ${next}`;
            case 'verified':
                return `Tu pago quedó en revisión manual: el banco lo confirmó, pero los montos requieren una revisión de FINA. ${next}`;
            case 'skipped_staging':
                return `Tu pago quedó en revisión manual: la verificación bancaria no está disponible en este ambiente. ${next}`;
            case 'error':
                return `Tu pago quedó en revisión manual: no pudimos confirmarlo con el banco en este momento. ${next}`;
            default:
                return `Tu pago quedó en revisión manual. ${next}`;
        }
    }

    private showRegistrationToast(title: string | undefined, message: string): void {
        if (this.paymentUnderReview) {
            this.toastService.alert('Pago en revisión', message, 8000);
            return;
        }
        this.toastService.success(title || 'Operación exitosa', message);
    }

    private showRegistrationError(err: any, fallbackMessage: string): void {
        const conflict = err?.status === 409;
        // El 409 de mid-cycle trae un mensaje interno ("la factura origen…"): lo
        // reemplazamos por la salida real que tiene el usuario.
        if (conflict && err?.error?.title === MID_CYCLE_NOT_ALLOWED_TITLE) {
            this.toastService.error('Cambio no disponible', PENDING_BILL_BLOCKS_PLAN_CHANGE_MESSAGE);
            return;
        }
        // Mix con cupos y la fecha de pago vencida: copy público de api-admin tal cual. El
        // carrito se conserva (no se emite `mixPurchaseRegistered`): los cupos van en la renovación.
        if (conflict && err?.error?.errorCode === EXTRA_USERS_MIX_RENEWAL_DUE) {
            this.toastService.alert(err.error.title || 'Renovación pendiente', err.error.message || EXTRA_USERS_MIX_RENEWAL_DUE_MESSAGE, 8000);
            return;
        }
        // Cupo fiscal (ADM-740): los cuatro 409 traen copy público; no son un rechazo del pago.
        if (fiscalQuotaIssue(err, { intentSent: false })) {
            this.toastService.alert(err.error.title || 'Cambio de facturas no disponible', err.error.message || fallbackMessage, 8000);
            return;
        }
        // Venta de cupos apagada (ADM-790): un cliente desfasado pidió cupos. Aviso con el copy
        // del servidor; el catch del pago de plan ya refresca el bundle en todo 409.
        if (isExtraUsersSalesDisabledError(err)) {
            this.toastService.alert(err.error.title || EXTRA_USERS_SALES_DISABLED_TITLE, err.error.message || EXTRA_USERS_SALES_DISABLED_MESSAGE, 8000);
            return;
        }
        // Solicitud gratuita activa (ADM-827): un carrito desfasado trajo números de control. Aviso
        // con el copy del servidor y recarga del estado, que saca el paquete del carrito de «Mejora tu Fina».
        if (isControlNumbersFreeModeError(err)) {
            this.toastService.alert(err.error.title || CONTROL_NUMBERS_FREE_MODE_TITLE, err.error.message || CONTROL_NUMBERS_FREE_MODE_MESSAGE, 8000);
            this.controlNumbersStatus.refresh();
            return;
        }
        const title = err?.error?.title || (conflict ? 'Conflicto al registrar el pago' : 'Error');
        const message = err?.error?.message || (conflict ? 'El pago cambió o ya fue registrado con otros datos. Actualiza la información antes de reintentar.' : fallbackMessage);
        this.toastService.error(title, message);
    }

    private getSmsPurchaseFallbackMessage(status?: string): string {
        if (status === 'pago') {
            return 'Pago confirmado y mensajes SMS acreditados.';
        }
        if (status === 'verificando-por-fina') {
            return 'Pago confirmado. Los mensajes SMS se acreditarán al finalizar la verificación de datos.';
        }
        return 'Compra de SMS reportada. Se acreditarán cuando el pago sea confirmado.';
    }

    onChangePayment(id: string, index: number) {
        if (this.blockedByUnresolvedDebit()) {
            return;
        }
        this.cardMethodSelected = false;
        this.debitoInmediatoSelected = false;
        this.pagoMovilSelected = true;
        this.payFormData = {
            ...this.payFormData,
            paymentMethod: id,
        };
        this.indexSelected = index;
        const selected: PlansPaymentMethod = this.paymentMethods.filter((pm: any) => pm._id == this.payFormData.paymentMethod)[0];
        this.paymentMethodSelected = selected;

        // Reset validation state when payment method changes
        this.resetValidationState();
        // Mix: por banco los cupos se cotizan con el día del pago, con tarjeta con el reloj.
        if (this.isMixPurchaseFlow) {
            this.syncMixAmountsFromCart();
        }

        // this.modalService.open(this.payData, { size: 'sm', centered: true, backdrop : 'static', keyboard : false,});
    }

    /**
     * El paso 2 no admite cambiar de método: o el pago ya se validó, o hay un
     * débito sin resolver que no se puede abandonar por otra vía de pago.
     */
    get paymentMethodLocked(): boolean {
        return this.isValidated || this.debitoInmediatoUnresolved || this.debitoInmediatoChargeInFlight;
    }

    /**
     * Los comercios recién migrados no pueden pagar hasta cargar su RIF: sin él
     * no se les puede emitir la factura fiscal del pago que están por hacer.
     * Se apaga en cuanto guardan los datos (el backend deja de pedirlos).
     */
    get isBlockedByMissingFiscalInfo(): boolean {
        return this.migrationNotice.requiresFiscalInfo;
    }

    /** Bloquea las tres vías de pago a la vez (pago móvil, débito y tarjeta). */
    get canPickPaymentMethod(): boolean {
        return !this.paymentMethodLocked && !this.isBlockedByMissingFiscalInfo;
    }

    /** Elegibilidad de la tarjeta en dólares: tenant jurídico (plus). Sin flag de PostHog: una caída de /flags no puede ocultarla. */
    isCardMethodEligible(): boolean {
        return this.commerceService.commerce?.plus === true;
    }

    /**
     * Carga (sin bloquear) si el tenant tiene una tarjeta activa. Si la tiene,
     * preseleccionamos la tarjeta al entrar al paso 2 y ocultamos los otros
     * métodos tras "Cambiar método de pago". Solo se consulta si es elegible.
     */
    private loadSavedCardState(): void {
        // Emite al pipeline de `ngOnInit`, que aplica `switchMap` (cancela el request
        // anterior) + `catchError`. La elegibilidad se evalúa dentro del pipeline.
        this.loadCard$.next();
    }

    /** Muestra el resto de métodos de pago (pago móvil) ocultos tras la tarjeta guardada. */
    revealAllPaymentMethods(): void {
        this.showAllMethods = true;
    }

    /**
     * Selecciona "Tarjeta en dólares" como método de pago. Deselecciona el pago
     * móvil (el cobro es en USD) y delega la captura/cobro al componente
     * `fina-auto-pay-card`.
     */
    selectCardMethod(): void {
        if (!this.canUseCardForSelection() || this.blockedByUnresolvedDebit()) {
            return;
        }
        this.cardMethodSelected = true;
        this.pagoMovilSelected = false;
        this.debitoInmediatoSelected = false;
        this.indexSelected = -1;
        this.paymentMethodSelected = null;
        this.payFormData = { ...this.payFormData, paymentMethod: '' };
        this.resetValidationState();
        // Mix: la tarjeta cobra en el acto, así que los cupos se cotizan con el reloj.
        if (this.isMixPurchaseFlow) {
            this.syncMixAmountsFromCart();
        }
    }

    /**
     * Elegibilidad del débito inmediato: tenant `plus`, sin flag de PostHog.
     *
     * Pide `plus` igual que la tarjeta. El método de pago con el que se registra
     * el cobro (`Débito Inmediato (BNC)`) está marcado `plus: true`, y el resto
     * del modal nunca le ofrece a un comercio un método cuyo `plus` no coincida
     * con el suyo: dejarlo pasar acá era la única puerta por la que un comercio
     * no-`plus` podía terminar pagando con un método `plus`.
     *
     * No aplica a la compra de SMS: el cobro va contra una factura de plan.
     */
    isDebitoInmediatoEligible(): boolean {
        // Cotiza y cobra la factura del plan (`billPlanId`): no sabe cobrar SMS ni el carrito mix.
        return !this.isSmsPurchaseFlow && !this.isMixPurchaseFlow && this.commerceService.commerce?.plus === true;
    }

    /**
     * Selecciona "Débito inmediato". El monto NO sale de este formulario: lo
     * cotiza el servidor a partir de la factura y del cambio de plan/frecuencia
     * elegido, porque el banco ata la clave al monto exacto en bolívares.
     */
    selectDebitoInmediatoMethod(): void {
        if (!this.isDebitoInmediatoEligible()) {
            return;
        }
        this.debitoInmediatoSelected = true;
        this.cardMethodSelected = false;
        this.pagoMovilSelected = false;
        this.indexSelected = -1;
        this.paymentMethodSelected = null;
        this.payFormData = { ...this.payFormData, paymentMethod: '' };
        this.resetValidationState();
    }

    /** El débito quedó registrado: mismo desenlace que el cobro con tarjeta. */
    onDebitoInmediatoPaid(): void {
        this.onCardPaid(null);
    }

    /**
     * El débito no cerró limpio (indeterminado, o debitado sin registrar). El
     * dinero pudo haber salido de la cuenta del cliente, así que el bloque del
     * débito se queda en pantalla: es el único lugar donde está la referencia y
     * la única salida segura (terminar de registrar ESE débito).
     *
     * Desde acá no se ofrece ninguna otra vía de pago ni se puede cambiar de
     * método: hacerlo destruye el componente y, con él, la referencia, e invita
     * a pagar por segunda vez algo que el banco quizá ya debitó.
     */
    onDebitoInmediatoUnresolved(): void {
        this.postPaymentSurveyStep = false;
        this.debitoInmediatoUnresolved = true;
        this.refreshAfterCardCharge();
    }

    /** El hijo avisa cuándo hay un débito en vuelo (cobro o espera del banco). */
    onDebitoInmediatoChargeInFlight(inFlight: boolean): void {
        this.debitoInmediatoChargeInFlight = inFlight;
    }

    /**
     * Guarda de las salidas del paso 2 mientras hay un débito en vuelo o sin
     * resolver. Es un callejón a propósito: destruir el bloque del débito corta
     * el seguimiento de un pago que ya puede existir, y dejaría la modal lista
     * para reportar un segundo pago de la misma factura.
     */
    private blockedByUnresolvedDebit(): boolean {
        if (this.debitoInmediatoChargeInFlight) {
            this.toastService.error('Pago en curso', 'Tu banco está procesando el débito. Espera el resultado antes de cambiar de método o salir.');
            return true;
        }
        if (!this.debitoInmediatoUnresolved) {
            return false;
        }
        this.toastService.error('Pago sin resolver', 'Tu banco pudo haber debitado este pago. No pagues por otra vía: termina de registrarlo o escríbenos con la referencia.');
        return true;
    }

    clearform() {
        this.payFormData = { ...initialPayData };
        this.pendingExtraControlNumbers = null;
        this.resetPaymentDateToToday();
        const paymentDate = this.toIsoDate(this.fromDate);
        this.payFormData.paymentDate = paymentDate;
        this.payFormData.paidDate = paymentDate;
        this.resetValidationState();
        this.loading = false;
        this.postPaymentSurveyStep = false;
        this.planPaymentSuccessMessage = '';
        this.destroyPayPlanConfetti();
        this.payModalStep = 1;
        this.isSmsPurchaseFlow = false;
        this.selectedSmsPackage = null;
        this.smsPurchaseSuccessMessage = '';
        this.setSelectedPlanToCurrent();
        this.selectedPaymentFrequency = 'monthly';
        this.resetCouponState();
        // Sin método preseleccionado al reabrir la modal.
        this.cardMethodSelected = false;
        this.pagoMovilSelected = false;
        this.debitoInmediatoSelected = false;
        this.debitoInmediatoUnresolved = false;
        this.debitoInmediatoChargeInFlight = false;
        this.indexSelected = -1;
        this.paymentMethodSelected = null;
        this.payFormData.paymentMethod = '';
        // `hasSavedCard` es estado del tenant (no del formulario): no lo reseteamos
        // al cerrar; se refresca en la próxima apertura vía `loadSavedCardState()`.
        this.showAllMethods = false;
    }

    resetValidationState() {
        this.isValidated = false;
        this.isValidating = false;
        this.validationAttempts = 0;
        this.manualReviewAttempts = DEFAULT_MANUAL_REVIEW_ATTEMPTS;
        this.validationTimer = 0;
        this.validationError = '';
        this.canSkipValidation = false;
        this.skipDueToAmountMismatch = false;
        this.skipDueToStaging = false;
        this.skipDueToApplicationReview = false;
        this.acceptedValidationFingerprint = null;
        this.validationRequestVersion++;
        if (this.validationTimerInterval) {
            clearInterval(this.validationTimerInterval);
            this.validationTimerInterval = null;
        }
    }

    onReferenceInput(event: Event) {
        const input = event.target as HTMLInputElement;
        const sanitizedValue = (input.value || '').replace(/\D/g, '').slice(0, 6);

        if (this.payFormData.ref !== sanitizedValue && this.hasValidationState()) {
            this.resetValidationState();
        }
        input.value = sanitizedValue;
        this.payFormData.ref = sanitizedValue;
    }

    onlyAllowReferenceNumbers(event: KeyboardEvent): boolean {
        if (event.key.length > 1 || event.ctrlKey || event.metaKey) {
            return true;
        }

        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
            return false;
        }

        return true;
    }

    open(content: TemplateRef<any>, modalOptions: NgbModalOptions = { size: 'lg', centered: true }) {
        if (this.activePayModalRef) {
            return;
        }

        this.payModalStep = 1;
        this.setSelectedPlanToCurrent();
        this.selectedPaymentFrequency = this.mapBundlePaymentFrequencyToUi();
        // Antes de `getPlans()`: la cantidad arranca en la de por defecto y `viewed` vuelve a contar.
        this.pendingExtraControlNumbers = null;
        this.fiscalOfferViewedTracked = false;

        this.postPaymentSurveyStep = false;
        this.resetValidationState();
        this.getPlans();
        this.hydrateSealedCouponFromBill();
        // No reseteamos `hasSavedCard` a false: es estado a nivel de tenant, no del
        // formulario. Conservamos el último valor conocido (de ngOnInit / apertura
        // previa) y lo refrescamos en segundo plano, para que `goToPaymentStep` ya
        // tenga el valor correcto aunque el usuario avance antes de que responda el HTTP.
        this.showAllMethods = false;
        this.showPlanList = false;
        this.paymentUnderReview = false;
        this.loadSavedCardState();
        this.resetPendingPurchasedUsers();

        const merged: NgbModalOptions = {
            ...modalOptions,
            windowClass: ['fpp-pay-modal', modalOptions.windowClass].filter(Boolean).join(' ').trim(),
        };

        this.activePayModalRef = this.modalService.open(content, merged);
        // `getPlans()` corrió antes de tener el modal abierto: aquí cuenta la vista del selector.
        this.trackFiscalOfferViewed();
        const cleanupAfterModalSettles = () => {
            // Antes de soltar `mixCart`: cerrar el checkout mix sin resultado terminal es abandono.
            if (this.modalMode === 'mix' && this.mixCart && !this.mixCheckoutSettled) {
                this.mixCheckoutSettled = true;
                this.analyticsService.analyticsCapture(MIX_PURCHASE_EVENTS.abandoned, {
                    ...this.mixAnalyticsProps(),
                    step: this.payModalStep,
                    payment_channel_selected: this.cardMethodSelected ? 'card' : this.pagoMovilSelected ? 'pago_movil' : null,
                });
            }
            this.activePayModalRef = null;
            this.postPaymentSurveyStep = false;
            this.modalMode = 'pay';
            this.activePlanSmsTab = 'plan';
            this.isSmsPurchaseFlow = false;
            this.mixCart = null;
            this.mixQuote = null;
            this.mixDayRate = null;
            this.refreshingMixRate = false;
            this.selectedSmsPackage = null;
            this.destroyPayPlanConfetti();
            setTimeout(() => {
                // Si reabrieron en estos 300 ms, no borrar el formulario nuevo.
                if (!this.activePayModalRef) {
                    this.clearform();
                }
            }, 300);
        };

        this.activePayModalRef.result.then(cleanupAfterModalSettles, cleanupAfterModalSettles);
    }

    /** Abre la modal en el flujo normal de pago (paso 1). Para lanzarla desde un botón externo. */
    openPayFlow(): void {
        this.open(this.payData, { size: 'lg', centered: true, backdrop: 'static', keyboard: false });
    }

    onPostPaymentSurveyCompleted(): void {
        this.postPaymentSurveyStep = false;
        this.goToSuccessStep();
    }

    private goToSuccessStep(): void {
        this.payModalStep = 3;
        if (this.paymentUnderReview) {
            return;
        }
        setTimeout(() => this.initializePayPlanConfetti());
    }

    /**
     * Pago con tarjeta guardada confirmado (fina-auto-pay-card). Refresca el bundle
     * de planes (o el commerce para SMS) y muestra el paso de éxito.
     * `event` es obligatorio a propósito: con `fullTemplateTypeCheck`, un binding
     * `(paid)="onCardPaid()"` sin `$event` no compila (perdería `pendingReconciliation`).
     */
    onCardPaid(event: AutoPayCardPaidEvent | null): void {
        this.couponHoldPaymentInFlight = false;
        this.clearCouponHoldTimer();
        this.postPaymentSurveyStep = false;
        if (this.isSmsPurchaseFlow) {
            // Igual que el mix: con `pendingReconciliation` el cobro ocurrió pero los SMS se acreditan al conciliar.
            this.smsPurchaseSuccessMessage = event?.pendingReconciliation
                ? event.message || 'El pago se realizó y los mensajes SMS se acreditarán al conciliarse.'
                : 'Pago confirmado y mensajes SMS acreditados.';
        }
        if (this.isMixPurchaseFlow) {
            this.mixCheckoutSettled = true;
            this.analyticsService.analyticsCapture(MIX_PURCHASE_EVENTS.cardPaid, {
                ...this.mixAnalyticsProps(),
                payment_channel: 'card',
                pending_reconciliation: event?.pendingReconciliation === true,
            });
            // `pendingReconciliation` (ADM-732): el cobro ya ocurrió pero la acreditación se concilia
            // aparte. Es un éxito, no un rechazo, pero no se dice «acreditados».
            this.planPaymentSuccessMessage = event?.pendingReconciliation
                ? event.message || 'El pago se realizó y la acreditación de los complementos queda pendiente de conciliación.'
                : 'Pago confirmado y complementos acreditados.';
            this.mixPurchaseRegistered.emit();
        }
        this.refreshAfterCardCharge();
        this.goToSuccessStep();
    }

    /**
     * El cobro con tarjeta no cerró en línea: quedó en vuelo (`processing`) o sin
     * confirmar (`unknown`). No hay éxito que mostrar —nada de paso 3 ni confeti—,
     * solo dejar el estado real a la vista y una salida:
     *   - `processing`: se confirma solo, así que cerramos la modal.
     *   - `unknown`: la factura necesita revisión; mostramos el pago móvil sin
     *     deseleccionar la tarjeta (eso destruiría el aviso del componente hijo).
     */
    onCardChargeUnresolved(outcome: CardChargeUnresolvedOutcome): void {
        this.couponHoldPaymentInFlight = false;
        this.postPaymentSurveyStep = false;
        if (this.isMixPurchaseFlow) {
            // No es rechazo ni éxito conciliado: se resuelve por webhook en api-admin.
            this.mixCheckoutSettled = true;
            this.analyticsService.analyticsCapture(MIX_PURCHASE_EVENTS.cardPending, { ...this.mixAnalyticsProps(), outcome });
        }
        this.refreshAfterCardCharge();
        if (outcome === 'processing') {
            this.modalService.dismissAll();
            return;
        }
        this.showAllMethods = true;
    }

    /** Refresca historial + bundle (o commerce en SMS) tras un intento de cobro con tarjeta. */
    private refreshAfterCardCharge(): void {
        this.plansService.refreshPaymentHistory();
        if (this.isSmsPurchaseFlow || this.isMixPurchaseFlow) {
            this.commerceService
                .obtainCommerce()
                .pipe(take(1), takeUntil(this.destroy$))
                .subscribe({
                    next: commerce => {
                        this.commerceService.commerce = commerce;
                    },
                    error: () => {
                        // El saldo se refresca en la próxima carga.
                    },
                });
            this.plansService.obtainPlansBundle();
        } else {
            this.plansService.obtainPlansBundle();
        }
    }

    private initializePayPlanConfetti(): void {
        const confettiContainer = document.getElementById('pay-plan-confetti');
        if (!confettiContainer) {
            return;
        }

        this.destroyPayPlanConfetti();
        confettiContainer.innerHTML = '';
        this.payPlanConfettiAnimation = lottie.loadAnimation({
            container: confettiContainer,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            path: 'assets/animations/confetti.json',
        });
    }

    private destroyPayPlanConfetti(): void {
        if (this.payPlanConfettiAnimation) {
            this.payPlanConfettiAnimation.destroy();
            this.payPlanConfettiAnimation = null;
        }
    }

    /** Paso 1 → 2 (extraer lógica con el bloque PAY_MODAL_STEP_1). */
    goToPaymentStep(): void {
        if (!this.canAdvanceWithCoupon()) {
            return;
        }
        if (!this.canProceedToPayment()) {
            if (this.isPlanChangeBlockedByPendingBill()) {
                this.toastService.error('Cambio no disponible', PENDING_BILL_BLOCKS_PLAN_CHANGE_MESSAGE);
                return;
            }
            this.toastService.error('Cambio requerido', 'Debes cambiar el plan o la frecuencia para continuar.');
            return;
        }
        this.isSmsPurchaseFlow = false;
        this.syncAmountsFromSelection();
        // El cupón se sella ANTES de entrar al paso 2, no al pulsar "Pagar": el cobro
        // con tarjeta lo dispara `fina-auto-pay-card` desde dentro del paso 2, y los
        // tres caminos de cobro leen el monto de la factura. Si el sellado falla, no
        // se avanza: el mensaje queda en el campo del paso 1.
        this.ensureCouponSealed$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(sealed => {
                if (sealed) {
                    this.enterPaymentStep();
                }
            });
    }

    /** Segunda mitad de `goToPaymentStep`: elección de método y salto al paso 2. */
    private enterPaymentStep(): void {
        // Si el tenant ya tiene tarjeta activa, preseleccionarla (soft-force):
        // el pago móvil queda tras el enlace "Cambiar método de pago".
        if (this.hasSavedCard && this.canUseCardForSelection()) {
            this.showAllMethods = false;
            this.selectCardMethod();
        } else {
            this.showAllMethods = true;
            if (this.cardMethodSelected) {
                this.cardMethodSelected = false;
                this.pagoMovilSelected = false;
                this.debitoInmediatoSelected = false;
                this.indexSelected = -1;
                this.paymentMethodSelected = null;
                this.payFormData.paymentMethod = '';
                this.resetValidationState();
            }
        }
        this.payModalStep = 2;
    }

    /** Paso 2 → 1 (extraer con PAY_MODAL_STEP_2). */
    goBackToPlanStep(): void {
        if (this.blockedByUnresolvedDebit()) {
            return;
        }
        this.cardMethodSelected = false;
        this.pagoMovilSelected = false;
        this.debitoInmediatoSelected = false;
        this.indexSelected = -1;
        this.paymentMethodSelected = null;
        this.payFormData = { ...this.payFormData, paymentMethod: '' };
        this.showAllMethods = false;
        this.resetValidationState();

        if (this.isMixPurchaseFlow) {
            this.modalService.dismissAll();
            return;
        }

        if (this.isSmsPurchaseFlow) {
            this.activePlanSmsTab = 'sms';
            this.syncSmsAmountsFromSelection();
        } else {
            this.getPlans();
        }

        this.payModalStep = 1;
    }

    /** Cierra tras éxito (extraer con PAY_MODAL_STEP_3). */
    acceptSuccessModal(): void {
        this.modalService.dismissAll();
    }

    selectPlan(planId: string): void {
        const wasFiscal = this.isFiscalDestination();
        const previousQuantity = this.getRequestedExtraControlNumbers();
        this.applyPlanSelection(planId);
        const selectedPlan = this.plans.find(plan => plan._id === planId);
        this.analyticsService.analyticsCapture('Plan cambiado', { planName: this.getPlanTypeLabel(selectedPlan?.type) });
        const isFiscal = this.isFiscalDestination();
        if (!wasFiscal && isFiscal) {
            this.trackFiscalOfferSelected(previousQuantity, this.getRequestedExtraControlNumbers());
        } else if (wasFiscal && !isFiscal) {
            this.trackFiscalOfferRemoved(previousQuantity);
        }
    }

    /**
     * Aplica la selección SIN registrarla en analítica. `selectPlan` es esto más el evento
     * 'Plan cambiado', que mide una elección del usuario: una preselección automática no debe
     * contarse como tal.
     */
    private applyPlanSelection(planId: string): void {
        this.selectedPlanId = planId;
        this.clampPendingPurchasedUsers();
        this.resetValidationState();
        this.revalidateCouponOnSelectionChange();
        this.syncAmountsFromSelection();
        this.trackFiscalOfferViewed();
    }

    /**
     * Preselecciona el plan inmediatamente superior al actual (Económico → Estándar). Lo pide el
     * límite semanal de Nina al mandar a actualizar el plan.
     *
     * Corre justo después de abrir: `open()` resetea la selección al plan actual de forma síncrona y
     * `getPlans()` ya no la toca si hay una. Si el plan no se puede cambiar desde el modal (factura
     * pendiente o cambio a mitad de ciclo sin resolver) no se fuerza nada y rige lo de siempre.
     */
    private preselectNextPlan(): void {
        const bundle = this.plansService.plansBundle$?.value;
        if (!bundle || !this.canChangePlanInModal(bundle)) return;
        const currentRank = this.getPlanCommercialOrder(bundle.currentPlan?.type);
        // `getSelectablePlans` ya viene ordenado por orden comercial y sin planes inferiores.
        const next = this.getSelectablePlans(bundle).find(plan => this.getPlanCommercialOrder(plan.type) > currentRank);
        if (next) this.applyPlanSelection(next._id);
    }

    selectCurrentPlan(bundle: PlansBundle): void {
        this.selectPlan(this.getCurrentPlanId(bundle));
    }

    isCurrentPlanSelected(bundle: PlansBundle): boolean {
        const currentPlanId = this.getCurrentPlanId(bundle);
        return !!currentPlanId && this.selectedPlanId === currentPlanId;
    }

    selectFrequency(id: PayPlanFrequencyId): void {
        this.selectedPaymentFrequency = id;
        this.resetValidationState();
        this.revalidateCouponOnSelectionChange();
        this.syncAmountsFromSelection();
        const selectedFrequency = this.paymentFrequencyOptions.find(option => option.id === id);
        this.analyticsService.analyticsCapture('Frecuencia de pago cambiada', { frequency: selectedFrequency?.label });
    }

    getSelectedPlan(bundle: PlansBundle): Plan | null {
        if (!bundle) {
            return null;
        }
        if (this.isCurrentPlanSelected(bundle)) {
            return bundle.currentPlan;
        }
        return this.findSelectedPlan(bundle) ?? bundle.currentPlan;
    }

    /** Badge, subtítulo y flag `plan-fiscal` del selector: el mismo predicado que el cupo fiscal. */
    isFiscalPlan(plan: Plan | null | undefined): boolean {
        return isFiscalOfferPlan(plan, this.getFiscalOffer());
    }

    isCurrentPlan(plan: Plan | null | undefined, bundle: PlansBundle): boolean {
        return !!plan?._id && plan._id === this.getCurrentPlanId(bundle);
    }

    getPlanTypeLabel(type: string | null | undefined): string {
        return type?.toLowerCase() === 'estandar' ? 'estándar' : type ?? '';
    }

    private getSelectablePlans(bundle: PlansBundle): Plan[] {
        const plansToImprove = bundle.plansToImprove ?? [];
        const catalog = plansToImprove.length > 0 ? plansToImprove : bundle.plans ?? [];
        // El plan actual entra aunque el catálogo ya no lo liste (tenant saltado, plan legacy).
        const selectablePlans = [bundle.currentPlan, ...catalog].filter((plan): plan is Plan => !!plan);
        const filteredPlans = this.filterOutLowerPlans(bundle, selectablePlans).filter(plan => !this.isEmptyPlan(plan, bundle));
        return this.sortPlansByCommercialOrder(this.uniquePlansById(filteredPlans));
    }

    /**
     * Plan sin `type` o sin `amount` numérico (p. ej. un fixture de QA en `plans`): no se
     * puede vender ni pintar, así que no se ofrece. El plan actual nunca se oculta.
     */
    private isEmptyPlan(plan: Plan, bundle: PlansBundle): boolean {
        if (this.isCurrentPlan(plan, bundle)) {
            return false;
        }
        return !plan.type?.trim() || typeof plan.amount !== 'number' || !Number.isFinite(plan.amount);
    }

    /** Si el plan actual es estándar/fiscal, no se ofrecen planes inferiores (evita degradar). */
    private filterOutLowerPlans(bundle: PlansBundle, plans: Plan[]): Plan[] {
        const currentRank = this.getPlanCommercialOrder(bundle.currentPlan?.type);
        if (currentRank <= 0) {
            return plans;
        }
        return plans.filter(plan => this.getPlanCommercialOrder(plan?.type) >= currentRank);
    }

    private findSelectedPlan(bundle: PlansBundle): Plan | null {
        const selectablePlan = this.getSelectablePlans(bundle).find(plan => plan._id === this.selectedPlanId);
        if (selectablePlan) {
            return selectablePlan;
        }
        return (bundle.plans ?? []).find(plan => plan._id === this.selectedPlanId) ?? null;
    }

    private getCurrentPlanId(bundle?: PlansBundle): string {
        bundle = bundle ?? this.plansService.plansBundle$?.value;
        return bundle?.currentPlan?._id ?? '';
    }

    private setSelectedPlanToCurrent(bundle?: PlansBundle): void {
        this.selectedPlanId = this.getCurrentPlanId(bundle);
    }

    private sortPlansByCommercialOrder(plans: Plan[]): Plan[] {
        return [...plans].sort((a, b) => this.getPlanCommercialOrder(a.type) - this.getPlanCommercialOrder(b.type));
    }

    private uniquePlansById(plans: Plan[]): Plan[] {
        return plans.filter((plan, index, self) => self.findIndex(candidate => candidate._id === plan._id) === index);
    }

    private getPlanCommercialOrder(type: string | null | undefined): number {
        const normalizedType = this.normalizePlanType(type);
        const order: Record<string, number> = {
            economico: 1,
            estandar: 2,
            fiscal: 3,
        };

        return order[normalizedType] ?? 99;
    }

    private normalizePlanType(type: string | null | undefined): string {
        return (type ?? '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim();
    }

    getSelectedPlanTitle(bundle: PlansBundle): string {
        const p = this.getSelectedPlan(bundle);
        return p?.type ? this.titleCasePlanType(p.type) : 'Plan';
    }

    getSelectedPlanSubtitle(bundle: PlansBundle): string {
        if (this.isCurrentPlanSelected(bundle)) {
            return 'Nuestro plan más popular para equipos pequeños.';
        }
        const p = this.getSelectedPlan(bundle);
        if (this.isFiscalPlan(p)) {
            return 'Facturación fiscal y reportes avanzados.';
        }
        return 'Actualiza cuando lo necesites.';
    }

    /** Descuento total USD: por frecuencia + cupón + saldo a favor (crédito en factura). */
    getSummaryDiscountUsd(bundle: PlansBundle): number {
        if (!bundle?.currentBillplan) {
            return 0;
        }
        if (this.isMidCyclePlanOnlyFee(bundle)) {
            return 0;
        }
        const freq = this.getFrequencyDiscountUsd(bundle);
        const coupon = this.getCouponDiscountUsd();
        const credit = this.getCreditDiscountUsd(bundle);
        return Number((freq + coupon + credit).toFixed(2));
    }

    /** Subtotal USD del período antes de descuentos por frecuencia y saldo. */
    getSummarySubtotalUsd(bundle: PlansBundle): number {
        if (!bundle?.currentBillplan) {
            return 0;
        }
        if (this.isMidCyclePlanOnlyFee(bundle)) {
            return this.getMidCyclePlanOnlyFeeBaseUsd(bundle);
        }
        const monthly = this.getMonthlyBaseUsd(bundle);
        const months = this.getMonthsForFrequency();
        return Number((monthly * months).toFixed(2));
    }

    /**
     * Total USD del resumen del paso 1 (neto + IVA), alineado con `syncAmountsFromSelection`.
     *
     * `bill.amount + Δ` solo sin cambio de plan ni frecuencia: con cambio, la factura
     * trae el precio del plan viejo y api-admin valida contra la cotización completa
     * (plan nuevo + todos los cupos), que es la rama de fórmula. Con cupón,
     * `desglose.totalUsd` ya trae los cupos facturados: solo se le suma el Δ.
     * El cupo fiscal (ADM-740) sigue la misma regla: con intención y sin cambio, el `patch`
     * de api-admin valida `bill.amount + Δcupos + Δfiscal`.
     */
    getSummaryTotalUsd(bundle: PlansBundle): number {
        const extrasDelta = this.getExtraUsersDeltaAmountUsd(bundle);
        const fiscalDelta = this.getExtraControlNumbersDelta(bundle).deltaAmount;
        const desglose = this.activeCouponDesglose;
        if (desglose) {
            return Number((Number(desglose.totalUsd ?? 0) + extrasDelta + fiscalDelta).toFixed(2));
        }
        const seatsPatch = this.canEditExtraUsers(bundle) && extrasDelta !== 0;
        const fiscalPatch = this.hasExtraControlNumbersIntent(bundle);
        if ((seatsPatch || fiscalPatch) && bundle?.currentBillplan && !this.hasPlanSelectionChanged(bundle) && !this.hasFrequencySelectionChanged(bundle)) {
            return Number((Number(bundle.currentBillplan.amount || 0) + extrasDelta + fiscalDelta).toFixed(2));
        }
        const netUsd = this.getNetAmountBeforeIvaUsd(bundle);
        const ivaUsd = this.getSummaryIvaUsd(bundle);
        return Number((netUsd + ivaUsd).toFixed(2));
    }

    /** Etiqueta corta del período para el banner del paso 2 (pago). */
    getFrequencyPeriodLabel(): string {
        switch (this.selectedPaymentFrequency) {
            case 'quarterly':
                return 'por trimestre';
            case 'semiannual':
                return 'por semestre';
            case 'yearly':
                return 'por año';
            default:
                return '/mes';
        }
    }

    /** Paso 2: el método de pago define si se muestra el total en USD o VES (misma lógica que validateReference). */
    isStep2PaymentCurrencyVes(): boolean {
        return this.paymentMethodSelected?.currency?.code === 'VES';
    }

    /** Monto total a pagar en la moneda del método seleccionado. */
    getAmountInPaymentMethodCurrency(): number {
        return this.isStep2PaymentCurrencyVes() ? Number(this.payFormData.amountVES ?? 0) : Number(this.payFormData.amount ?? 0);
    }

    /** Código ISO para el pipe `currency` en paso 2. */
    getStep2CurrencyCode(): string {
        return this.isStep2PaymentCurrencyVes() ? 'VES' : 'USD';
    }

    /** Locale para formatear montos del paso 2. */
    getStep2CurrencyLocale(): string {
        return this.isStep2PaymentCurrencyVes() ? 'es-VE' : 'en-US';
    }

    /**
     * Formatea un monto ya en la moneda del paso 2: VES como "Bs. 1.234,56"
     * (prefijo, formato es-VE) y USD como "$1,234.56". `digits` es el patrón del
     * pipe currency/number (p. ej. "1.2-2" o "1.0-0").
     */
    formatStep2Amount(value: number, digits: string): string {
        const amount = Number(value ?? 0);
        if (this.isStep2PaymentCurrencyVes()) {
            return `Bs. ${formatNumber(amount, 'es-VE', digits)}`;
        }
        return formatCurrency(amount, 'en-US', '$', 'USD', digits);
    }

    /** Copia un valor al portapapeles y muestra un toast de confirmación. */
    copyToClipboard(value: string | number | null | undefined, label: string, digitsOnly = false): void {
        let text = value === null || value === undefined ? '' : String(value).trim();
        if (digitsOnly) {
            text = text.replace(/\D/g, '');
        }
        if (!text) {
            return;
        }
        const notifySuccess = () => this.toastService.success('Copiado', `${label} copiado al portapapeles.`);
        const notifyError = () => this.toastService.error('No se pudo copiar', 'Cópialo manualmente.');

        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(text).then(notifySuccess).catch(notifyError);
            return;
        }

        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            textarea.remove();
            notifySuccess();
        } catch {
            notifyError();
        }
    }

    /** Convierte un monto en USD a la moneda mostrada en paso 2 (USD sin cambio; VES × tasa BCV). */
    getUsdAsStep2DisplayCurrency(usd: number): number {
        const n = Number(usd ?? 0);
        if (!this.isStep2PaymentCurrencyVes()) {
            return Number(n.toFixed(2));
        }
        const rate = Number(this.payFormData.exchangeRateBCV || 0);
        return Number((n * rate).toFixed(2));
    }

    getSummaryDiscountBs(bundle: PlansBundle): number {
        const d = this.getSummaryDiscountUsd(bundle);
        return Number((d * (this.payFormData.exchangeRateBCV || 0)).toFixed(2));
    }

    getSummarySubtotalBs(bundle: PlansBundle): number {
        return Number((this.payFormData.amountVES + this.getSummaryDiscountBs(bundle)).toFixed(2));
    }

    private titleCasePlanType(type: string): string {
        return type.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    }

    _getDismissReason(reason: unknown): string {
        if (reason === ModalDismissReasons.ESC) {
            return 'by pressing ESC';
        } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
            return 'by clicking on a backdrop';
        } else {
            return `with: ${reason}`;
        }
    }

    // Date
    onDateSelection(date: NgbDate | null, datepicker: { close?: () => void } | null) {
        // SMS suelto: buy-sms fuerza la fecha de hoy. La mix sí reporta la fecha elegida (ADM-753).
        if (this.isSmsPurchaseFlow) {
            return;
        }

        const previousDate = this.normalizePaymentDate(this.payFormData.paymentDate);
        const selectedDate = date || this.fromDate;

        if (!selectedDate || selectedDate.after(this.maxDate)) {
            return;
        }

        if (date) {
            this.fromDate = date;
            datepicker?.close?.();
        }

        const fromDateToSend = this.toIsoDate(selectedDate);
        if (!fromDateToSend) {
            return;
        }

        if (previousDate !== fromDateToSend && this.hasValidationState()) {
            this.resetValidationState();
        }

        this.payFormData = {
            ...this.payFormData,
            paymentDate: fromDateToSend,
            paidDate: fromDateToSend,
        };
        this.paymentDateInput = this.formatter.format(this.fromDate);

        // Mix por banco: el día del pago fija la tasa BCV y los meses de cupos. `previousDate` se
        // leyó antes de reasignar `payFormData` (`onPaymentDateInput` ya movió `fromDate`).
        if (this.isMixPurchaseFlow && previousDate !== fromDateToSend) {
            this.syncMixAmountsFromCart();
            this.mixRateDay$.next(fromDateToSend);
        }
    }

    onPaymentDateInput(event: Event) {
        const input = event.target as HTMLInputElement;
        this.paymentDateInput = input.value;
        const currentDate = this.fromDate;
        const parsedDate = this.validateInput(currentDate, input.value);
        this.fromDate = parsedDate;

        if (this.toIsoDate(parsedDate) !== this.toIsoDate(currentDate)) {
            this.onDateSelection(null, null);
        }
    }

    isHovered(date: NgbDate) {
        return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
    }

    isInside(date: NgbDate) {
        return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
    }

    isRange(date: NgbDate) {
        return date.equals(this.fromDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date) || this.isHovered(date);
    }

    validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
        const parsed = this.formatter.parse(input);
        if (!parsed) {
            return currentValue;
        }

        const parsedDate = NgbDate.from(parsed);
        return this.calendar.isValid(parsedDate) && !parsedDate.after(this.maxDate) ? parsedDate : currentValue;
    }

    canValidatePayment(): boolean {
        return this.paymentMethodSelected?._id === environment.VALIDATABLE_BANK_ACCOUNT_ID;
    }

    isValidationRequired(): boolean {
        if (!this.canValidatePayment()) {
            return false;
        }
        const validationAccepted = this.isValidated || this.canSkipValidation;
        return !validationAccepted || !this.isValidationFingerprintCurrent();
    }

    private hasValidationState(): boolean {
        return this.isValidated || this.isValidating || this.validationAttempts > 0 || this.canSkipValidation || Boolean(this.validationError);
    }

    toIsoDate(date: NgbDate | null): string | undefined {
        if (!date) {
            return undefined;
        }

        const month = String(date.month).padStart(2, '0');
        const day = String(date.day).padStart(2, '0');
        return `${date.year}-${month}-${day}`;
    }

    private normalizePaymentDate(value: unknown): string | undefined {
        if (!value) {
            return undefined;
        }

        const parsed = moment(value);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
    }

    private buildExpectedDate(): string | undefined {
        const expectedDate = this.toIsoDate(this.fromDate);
        if (expectedDate) {
            return expectedDate;
        }
        if (this.payFormData.paymentDate) {
            return this.normalizePaymentDate(this.payFormData.paymentDate);
        }
        return undefined;
    }

    private resetPaymentDateToToday(): void {
        this.maxDate = this.calendar.getToday();
        this.fromDate = new NgbDate(this.maxDate.year, this.maxDate.month, this.maxDate.day);
        this.paymentDateInput = this.formatter.format(this.fromDate);
    }

    private buildVerifyPaymentRequest(): VerifyPaymentRequest {
        const currencyCode = this.paymentMethodSelected?.currency?.code;
        const amountUsd = Number(this.payFormData.amount ?? 0);
        const amountVes = Number(this.payFormData.amountVES ?? 0);
        const expectedAmount = currencyCode === 'VES' ? amountVes : amountUsd;
        const body: VerifyPaymentRequest = {
            paymentMethodId: this.paymentMethodSelected._id,
            reference: this.payFormData.ref,
            expectedAmount,
            expectedAmounts: { USD: amountUsd, VES: amountVes },
            expectedDate: this.buildExpectedDate(),
        };
        const exchangeRateBCV = Number(this.payFormData.exchangeRateBCV ?? 0);
        if (exchangeRateBCV > 0) {
            body.exchangeRateBCV = exchangeRateBCV;
        }
        if (!this.isSmsPurchaseFlow && !this.isMixPurchaseFlow && this.payFormData.billPlanId) {
            body.billPlanId = this.payFormData.billPlanId;
        }
        return body;
    }

    private buildValidationFingerprint(body: VerifyPaymentRequest = this.buildVerifyPaymentRequest()): string {
        return JSON.stringify({
            paymentMethodId: body.paymentMethodId,
            currency: this.paymentMethodSelected?.currency?.code ?? null,
            reference: body.reference,
            expectedAmount: body.expectedAmount,
            expectedAmounts: body.expectedAmounts,
            exchangeRateBCV: body.exchangeRateBCV ?? null,
            expectedDate: body.expectedDate ?? null,
            billPlanId: body.billPlanId ?? null,
            planId: this.isSmsPurchaseFlow ? null : this.selectedPlanId || null,
            paymentFrequency: this.isSmsPurchaseFlow ? null : this.selectedPaymentFrequency,
            smsQuantity: this.isSmsPurchaseFlow ? this.selectedSmsPackage?.quantity ?? null : null,
        });
    }

    private isValidationFingerprintCurrent(): boolean {
        return this.acceptedValidationFingerprint === this.buildValidationFingerprint();
    }

    private invalidateStaleValidation(): void {
        if ((this.isValidated || this.canSkipValidation) && !this.isValidationFingerprintCurrent()) {
            this.resetValidationState();
        }
    }

    private messageForOutcome(outcome: PaymentVerificationOutcome | undefined, data?: VerifyPaymentResponseData): string {
        switch (outcome) {
            case 'duplicate_reference':
                return 'Esta referencia ya fue registrada. Verifica el número e intenta nuevamente.';
            case 'not_found':
                return 'No se encontró el pago con esa referencia. Verifica los datos e intenta nuevamente.';
            case 'date_mismatch':
                return 'La fecha del pago no coincide con la fecha indicada. Corrige la fecha e intenta nuevamente.';
            case 'invalid_expected_amount':
                return 'El monto esperado no es válido. Revisa el monto del plan e intenta nuevamente.';
            case 'not_verifiable':
                return 'Este método de pago no permite verificación automática.';
            case 'amount_below_tolerance':
            case 'amount_mismatch':
                return data?.message || 'El monto encontrado no coincide con el esperado. Puedes continuar; tu pago será revisado manualmente.';
            case 'overpayment_within_tolerance':
            case 'overpayment_above_tolerance':
                return this.buildOverpaymentMessage(data);
            case 'error':
                return data?.message || 'Ocurrió un error al verificar el pago. Intenta nuevamente.';
            case 'skipped_staging':
                return 'La verificación bancaria no está disponible en este ambiente. Puedes continuar; tu pago será revisado manualmente.';
            default:
                return 'No se pudo verificar el pago. Por favor, verifica los datos e intenta nuevamente.';
        }
    }

    private buildOverpaymentMessage(data?: VerifyPaymentResponseData): string {
        return data?.message || 'Se detectó un pago mayor al esperado. Tu pago quedará pendiente de revisión manual.';
    }

    private storeVerificationMetadata(data?: VerifyPaymentResponseData) {
        if (typeof data?.manualReviewAttempts === 'number' && data.manualReviewAttempts > 0) {
            this.manualReviewAttempts = data.manualReviewAttempts;
        }
    }

    validateReference() {
        if (!this.payFormData.ref || !/^\d{6}$/.test(this.payFormData.ref) || !this.canValidatePayment() || !this.paymentMethodSelected?._id) {
            return;
        }

        this.isValidating = true;
        this.validationError = '';
        this.skipDueToAmountMismatch = false;
        this.skipDueToStaging = false;

        const body = this.buildVerifyPaymentRequest();
        const validationFingerprint = this.buildValidationFingerprint(body);
        const requestVersion = this.validationRequestVersion;

        this.plansService
            .verifyPayment$(body)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: response => {
                    if (requestVersion !== this.validationRequestVersion) {
                        return;
                    }

                    this.isValidating = false;
                    const data = response?.data;

                    if (response?.success && data?.autoApprove === true && data?.applicationStatus !== 'manual_review') {
                        this.isValidated = true;
                        this.validationError = '';
                        this.canSkipValidation = false;
                        this.skipDueToAmountMismatch = false;
                        this.skipDueToStaging = false;
                        this.skipDueToApplicationReview = false;
                        this.acceptedValidationFingerprint = validationFingerprint;
                        return;
                    }

                    this.handleVerificationOutcome(data, validationFingerprint);
                },
                error: () => {
                    if (requestVersion !== this.validationRequestVersion) {
                        return;
                    }

                    this.isValidating = false;
                    this.handleVerificationOutcome({ outcome: 'error' } as VerifyPaymentResponseData, validationFingerprint);
                },
            });
    }

    private unlockManualReview(options: { amountMismatch?: boolean; staging?: boolean; applicationReview?: boolean } = {}, validationFingerprint: string = this.buildValidationFingerprint()) {
        this.canSkipValidation = true;
        this.skipDueToAmountMismatch = Boolean(options.amountMismatch);
        this.skipDueToStaging = Boolean(options.staging);
        this.skipDueToApplicationReview = Boolean(options.applicationReview);
        this.acceptedValidationFingerprint = validationFingerprint;
    }

    private handleVerificationOutcome(data: VerifyPaymentResponseData | undefined, validationFingerprint: string) {
        this.isValidated = false;
        this.storeVerificationMetadata(data);
        const outcome = data?.outcome;

        if (data?.applicationStatus === 'manual_review') {
            this.unlockManualReview({ applicationReview: true }, validationFingerprint);
            this.validationError = data.summary || data.message || 'El banco validó el pago, pero los montos requieren revisión manual de FINA.';
            return;
        }

        if (outcome === 'skipped_staging') {
            this.unlockManualReview({ staging: true }, validationFingerprint);
            this.validationError = this.messageForOutcome(outcome, data);
            this.startValidationTimeout();
            return;
        }

        if (outcome && AMOUNT_MISMATCH_OUTCOMES.includes(outcome)) {
            this.unlockManualReview({ amountMismatch: true }, validationFingerprint);
            this.validationError = this.messageForOutcome(outcome, data);
            this.startValidationTimeout();
            return;
        }

        // duplicate (contrato) + never-verify / unknown: never isValidated;
        // count attempts so the user can still report payment manually.
        this.validationAttempts++;
        this.validationError = this.messageForOutcome(outcome, data);
        this.startValidationTimeout();
        if (this.validationAttempts >= this.manualReviewAttempts) {
            this.unlockManualReview({}, validationFingerprint);
        }
    }

    startValidationTimeout() {
        if (this.validationTimerInterval) {
            clearInterval(this.validationTimerInterval);
            this.validationTimerInterval = null;
        }

        this.validationTimer = 10;

        this.validationTimerInterval = setInterval(() => {
            this.validationTimer--;

            if (this.validationTimer <= 0) {
                clearInterval(this.validationTimerInterval);
                this.validationTimerInterval = null;
                this.validationTimer = 0;
            }
        }, 1000);
    }

    /**
     * True si el pago NO es reinicio (atraso ≤ 60 días): salta la encuesta.
     * False si atraso ≥ 61 días (mismo umbral que over_60 en api-admin:
     * daysLate > 60) o falta fecha: muestra la encuesta post-pago.
     */
    validateDatePlan(): boolean {
        const datePlan = this.lastPaymentDate ? this.lastPaymentDate : this.plansService.plansBundle$?.value.currentBillplan?.paymentDatePlan;
        if (!datePlan) {
            return false; // o el criterio que uséis si falta fecha
        }
        const plan = moment(datePlan);
        const today = moment().startOf('day');
        const daysDiff = plan.diff(today, 'days'); // futuro positivo, pasado negativo
        // Espejo de daysLate > 60 en api-admin (reinicio = 61+).
        if (daysDiff <= -61) {
            return false;
        }
        return true;
    }
}
